<template>
  <v-container class="ma-0 pa-0" fluid>
   <div class="d-flex align-stretch batch-flex">
    <div ref="batchGridRef" class="batch-grid flex-grow-1">
    <v-data-table
      :headers="displayHeaders"
      :height="tableSize"
      :items-per-page="pageSize"
      :items="items"
      :loading="loading"
      :search="search"
      :item-class="getRowClass"
      loading-text="Loading Items...Please Wait"
      loader-color="orange"
      dense
      fixed-header
      hide-default-footer
      item-key="id"
      :expanded="expanded"
      :show-expand="isTprProRetailUser && (storeGroupIsSelected && !isPercentageProofStoreGroup)"
      :single-expand="false"
      :show-select="!isEditingDisabled"
      :disable-sort="true"
      class="dark-dividers"
      ref="dataTable"
      v-model="selected">
      <template v-for="header in allHeaders" v-slot:[`header.${header.value}`]="">
        <div :key="header.value" @click="handleSort(header)">
          {{ header.text }}
          <v-icon v-if="header.showArrow && !header.descending" small color="white">mdi-chevron-up</v-icon>
          <v-icon v-if="header.showArrow && header.descending" small color="white">mdi-chevron-down</v-icon>
          <v-icon
            v-if="header.showArrow"
            color="red"
            small
            @click.stop="removeSort(header)"
            style="position: absolute; top: 0; right: 0; cursor: pointer;">
            mdi-close
          </v-icon>
        </div>
      </template>
      <template #top>
        <v-toolbar flat color="toolbar">
          <v-row dense align="center" class="flex-right">
            <!-- Store Group Info Button -->
            <v-btn
                v-if="storeGroupIsSelected && selectedStoreParty.party_type === 'TPR_STORE_GROUP'"
                height="40"
                depressed
                color="primary"
                style="border: 1px solid #9e9e9e; border-left: 0px; border-right: 0px"
                @click="storeGroupModal = true">
                <v-icon color="white">mdi-store</v-icon>
              </v-btn>

            <!-- Breadcrumbs and batch info string -->
            <v-col cols="4"
              @mouseenter="showExpanded=true"
              @mouseleave="showExpanded=false">
              <v-breadcrumbs large :items="breadcrumbs">
                <template #item="{ item }">
                  <v-breadcrumbs-item :href="item.href" :disabled="item.disabled">
                    <span :class="!item.disabled ? 'primary--text' : ''">{{
                        showExpanded && item.expanded_text ? item.expanded_text : item.text
                      }}</span>
                  </v-breadcrumbs-item>
                </template>
              </v-breadcrumbs>
            </v-col>
            <v-spacer />

            <!-- Warnings and Info menu -->
            <v-menu bottom offset-y nudge-bottom="5" :value="infoMenu" class="">
              <template v-slot:activator="{ on: menu , attrs}">
                <v-tooltip top>
                  <template v-slot:activator="{ on: tooltip }">
                    <v-badge v-show="hasWarnings" overlap :content="toolbarWarnings.length" bordered left :color="toolbarWarningsColor">
                      <v-btn :color="toolbarWarningsColor" outlined class="px-0" v-bind="attrs" v-on="{...tooltip, ...menu}" @click="infoMenu = true">
                        <v-icon class="px-0">mdi-information</v-icon>
                      </v-btn>
                    </v-badge>
                  </template>
                  <span>Info and Warnings</span>
                </v-tooltip>
              </template>
              <v-card outlined class="px-2 py-1">
                <v-alert 
                  v-for="(alert, index) in toolbarWarnings"
                  :key="index"
                  :type="alert.type"
                  dense
                  outlined
                  class="my-2">
                  {{ alert.message }}
                </v-alert>
              </v-card>
            </v-menu>

            <v-menu v-if="isFiltered" bottom open-on-hover max-height="300">
              <template v-slot:activator="{ on }">
                <v-chip v-on="on" class="orange white--text ml-2">
                  <v-icon color="white" class="mr-2" small>
                    mdi-filter-outline
                  </v-icon>
                  <span class="text-capitalize">
                    Results for {{ searchValues.length }} search values
                  </span>
                  <v-tooltip top>
                    <template #activator="{ on }">
                      <v-btn v-on="on" @click="searchValues = []" class="white--text ml-2" icon x-small>
                        <v-icon small>mdi-close-circle</v-icon>
                      </v-btn>
                    </template>
                    <span>Clear Filter</span>
                  </v-tooltip>
                </v-chip>
              </template>
              <v-card>
                <v-card-title class="text-subtitle-2 pb-0">
                  Search Values:
                </v-card-title>
                <v-list>
                  <v-list-item v-for="(value, index) in searchValues" :key="index">
                    {{ value }}
                  </v-list-item>
                </v-list>
              </v-card>
            </v-menu>

            <!-- Header Ordering Modal Button -->
            <v-btn v-if="tprProTenants || tenant == 'awg'" class="grey lighten-2" @click="openModal" :disabled="loading">
              <v-icon>mdi-table-edit</v-icon>
            </v-btn>

            <!-- Session Options Button -->
            <v-menu
              v-if="$auth.tenant === 'alliance-retail-group'"
              :value="sessionMenu"
              bottom offset-y nudge-bottom="5"
              >
              <template v-slot:activator="{ on: menu }">
                <v-tooltip top>
                  <template v-slot:activator="{ on: tooltip }">
                    <v-btn class="grey lighten-2"
                      v-on="{...tooltip, ...menu}"
                      @click="sessionMenu = true">
                      Session
                    </v-btn>
                  </template>
                  <span>Session controls</span>
                </v-tooltip>
              </template>
              <v-card color="white" class="d-flex flex-column px-2 py-1">
                  <v-tooltip right>
                    <template #activator="{ on }">
                      <v-btn  v-on="on" class="grey lighten-2 my-2" @click="saveCurrentSession">
                        Save Session
                      </v-btn>
                    </template>
                    <span>Save Current Session</span>
                  </v-tooltip>
                  <v-tooltip right>
                    <template #activator="{ on }">
                      <v-btn v-on="on" class="grey lighten-2 px-2 my-2" @click="triggerFileInput">
                        Load Session
                      </v-btn>
                    </template>
                    <span>Load Session State</span>
                  </v-tooltip>
              </v-card>
            </v-menu>
            <input
              v-if="$auth.tenant === 'alliance-retail-group'"
              ref="sessionFileInput"
              type="file"
              accept=".json"
              style="display: none"
              @change="handleFileSelect"
            />

            <!-- Header Ordering Modal -->
            <v-dialog v-model="showModal" max-width="600px" scrollable persistent>
              <v-card>
                <v-card-title class="main white--text pa-2 centered-title">
                  <span>Ordering Headers</span>
                </v-card-title>
                <v-card-text>
                  <v-form>
                    <v-container>
                      <div class="row">
                        <!-- Draggable List 1 (Visible Headers) -->
                        <div class="col-6">
                          <h3 class="header-column-name">Display Order</h3>
                          <draggable class="list-group" :list="displayHeaders" group="headers" @end="onDragEnd">
                            <div v-for="header in displayHeaders" :key="header.value">
                              <div v-if="header.value !== 'data-table-select'" class="list-group-item">
                                {{ header.text }}
                              </div>
                            </div>
                          </draggable>
                        </div>
                        <!-- Draggable List 2 (Hidden Headers) -->
                        <div class="col-6">
                          <h3 class="header-column-name">Hide</h3>
                          <draggable class="list-group" :list="hideHeaders" group="headers" @end="onDragEnd">
                            <div
                              class="list-group-item"
                              v-for="header in hideHeaders"
                              :key="header.value">
                              {{ header.text }}
                            </div>
                          </draggable>
                        </div>
                      </div>
                    </v-container>
                  </v-form>
                </v-card-text>
                <v-divider thickness="2"/>
                <v-card-actions class="grey lighten-4">
                  <v-spacer />
                  <v-btn color="main white--text" @click="resetHeaders">Reset</v-btn>
                  <v-btn color="green darken-1 white--text" @click="saveHeadersOrder">Save</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>

            <!-- Bulk Edit Button -->
            <v-menu
              :close-on-content-click="false"
              :nudge-bottom="3"
              :value="srpMenu"
              bottom>
              <template #activator="{ on: menu, attrs }">
                <v-tooltip top>
                  <template v-slot:activator="{ on: tooltip }">
                    <v-btn
                      :disabled="!selected.length || isEditingDisabled || isReadOnlyUser"
                      :readonly="!selected.length"
                      v-bind="attrs"
                      v-on="{ ...tooltip, ...menu }"
                      small
                      class="grey lighten-2"
                      height="38"
                      @click="openBulkMenu">
                      <v-icon>mdi-pencil-box-multiple-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Edit</span>
                </v-tooltip>
              </template>
              <v-card class="fill" width="200">
                <v-card-title class="pb-0 text-subtitle-1"> Edit </v-card-title>
                <v-form class="px-4 pt-4" ref="form">
                  <v-text-field :rules="multiplierRules" background-color="input" dense label="Multiplier" outlined
                    type="number" v-model="multiplier">
                  </v-text-field>
                  <v-text-field :rules="srpRules" background-color="input" dense label="SRP" outlined placeholder="0.00"
                    autofocus prefix="$" type="number" @blur="handleSRPBlur(null, $event)" v-model="srp"
                    @keydown.enter="bulkUpdate">
                  </v-text-field>
                </v-form>
                <v-card-actions class="fill">
                  <v-spacer />
                  <v-btn :class="body" @click="; (srp = ''), (srpMenu = false)" text>
                    Cancel
                  </v-btn>
                  <v-btn :class="body" @click="bulkUpdate" class="blue white--text mr-2">
                    Update
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-menu>

            <!-- Bulk delete button (PCE) -->
            <v-menu 
              v-if="tenant !== 'alliance-retail-group' && tenant !== 'awg'"
              :close-on-content-click="false"
              :nudge-bottom="3"
              :value="deleteMenu"
              bottom>
              <template #activator="{ on: menu, attrs }">
                <v-tooltip top>
                  <template v-slot:activator="{ on: tooltip }">
                    <v-btn 
                      :disabled="!selected.length || isEditingDisabled"
                      :readonly="!selected.length"
                      v-bind="attrs"
                      v-on="{ ...tooltip, ...menu }"
                      small
                      class="grey lighten-2"
                      height="38"
                      @click="deleteMenu = true">
                      <v-icon>mdi-trash-can-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Delete</span>
                </v-tooltip>
              </template>
              <v-card class="fill" width="200">
                <v-card-text align="center" class="black--text text-subtitle-1">
                  Delete {{ selected.length }} items?
                </v-card-text>
                <v-card-actions class="fill">
                  <v-spacer />
                  <v-btn :class="body" @click="deleteMenu = false" text>
                    Cancel
                  </v-btn>
                  <v-btn :class="body" @click="bulkDelete" class="red white--text mr-2">
                    Delete
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-menu>

            <!-- Save Button -->
            <v-tooltip top :disabled="!hasInvalidInputs">
              <template #activator="{ on }">
                <div v-if="tprProTenants" v-on="on" class="d-inline-block">
                  <v-btn
                    :loading="loading"
                    :disabled="hasInvalidInputs || isEditingDisabled || isReadOnlyUser"
                    color="success"
                    @click="prepareToSave">
                    <v-icon left>mdi-content-save</v-icon>
                    Save
                  </v-btn>
                </div>
              </template>
              <span>{{ saveBlockedReason }}</span>
            </v-tooltip>

            <!-- Save Method Toggle (ARG) -->
            <v-btn-toggle v-if="storeGroupIsSelected" v-model="saveMethodToggle" mandatory>
              <v-tooltip top>
                <template v-slot:activator="{ on: tooltip }">
                  <v-btn   v-on="{ ...tooltip}">
                    <v-icon>mdi-content-save-all</v-icon>
                  </v-btn>
                </template>
                <span>Automatically Save Changes to Child Rows</span>
              </v-tooltip>
              <v-tooltip top>
                <template v-slot:activator="{ on: tooltip }">
                  <v-btn v-on="{ ...tooltip}">
                    <v-icon>mdi-content-save</v-icon>
                  </v-btn>
                </template>
                <span>Save Only to Focal Row</span>
              </v-tooltip>
            </v-btn-toggle>

            <!-- Reset Changes Button (ARG) -->
            <v-btn color="red" :loading="loading"
              v-if="tprProTenants && hasActiveChanges" @click="refreshPage"
              class="ml-2">
              <v-icon left>mdi-refresh-circle</v-icon>
              Reset All
            </v-btn>

            <!-- Text Search Box (hidden for store-level retail users — only searches current page) -->
            <v-text-field
              v-if="!isTprProRetailUser"
              v-model="search"
              label="Search results"
              prepend-inner-icon="mdi-magnify"
              hide-details dense outlined clearable
              style="max-width: 230px;"
              class="pp-keyword-search" />
            
            <!-- Filters Side Bar button-->
            <v-btn class="grey lighten-2" height="38" @click="filterDrawer = !filterDrawer">
              <v-icon left>mdi-filter-variant</v-icon>
              Filters
              <v-badge v-if="activeFilterCount" inline :content="activeFilterCount" color="primary" class="ml-1" />
            </v-btn>
            <div style="width: 10px;"></div>

            <!-- Remove Custom Header Sort button -->
            <v-tooltip top>
              <template v-slot:activator="{ on: tooltip }">
                <v-btn v-show="showRemoveSortsButton" small class="red lighten-1" height="38" @click="removeAllSorts"
                  v-on="tooltip">
                  <v-icon color="white">mdi-sort-variant-remove</v-icon>
                </v-btn>
              </template>
              <span>Remove all custom ordering</span>
            </v-tooltip>
            
            <!-- Expand/Collapse All Button -->
            <v-tooltip top v-if="isTprProRetailUser && storeGroupIsSelected && !isPercentageProofStoreGroup">
              <template v-slot:activator="{ on: tooltip }">
                <v-btn small class="grey lighten-2 mr-1" height="38" @click="toggleExpandAll" v-on="tooltip">
                  <v-icon>{{ allExpanded ? 'mdi-collapse-all' : 'mdi-expand-all' }}</v-icon>
                </v-btn>
              </template>
              <span>{{ allExpanded ? 'Collapse All Rows' : 'Expand All Rows' }}</span>
            </v-tooltip>

            <!-- AI Insights Button -->
            <v-tooltip v-if="showAiInsightsButton" top>
              <template #activator="{ on }">
                <div v-on="on" class="d-inline-block mr-1">
                  <v-btn :disabled="!!aiInsightsBlockedReason" class="grey lighten-2" height="38"
                    data-test="ai-insights-button" @click="openAiInsights">
                    <v-icon left>mdi-creation</v-icon>
                    AI Insights
                  </v-btn>
                </div>
              </template>
              <span>{{ aiInsightsBlockedReason || 'Generate an AI analysis of this batch' }}</span>
            </v-tooltip>

            <!-- Batch Actions Button -->
            <v-menu bottom :nudge-bottom="3" :close-on-content-click="false" :value="actionMenu">
              <template #activator="{ on: menu, attrs }">
                <v-tooltip top>
                  <template v-slot:activator="{ on: tooltip }">
                    <v-btn :disabled="isSyncing" small class="grey lighten-2" height="38" v-bind="attrs"
                      v-on="{ ...tooltip, ...menu }" @click="actionMenu = true">
                      <v-icon>mdi-cog-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Batch Actions</span>
                </v-tooltip>
              </template>
              <v-card class="fill">
                <v-list class="fill">
                  <v-list-item v-for="(action, index) in actions" :key="index" link
                    @click="action.method(selectedBatch, primaryStore)">
                    <v-list-item-icon>
                      <v-icon>{{ action.icon }}</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>{{ action.text }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-card>
            </v-menu>
          </v-row>
        </v-toolbar>
        <v-divider />
      </template>

      <!-- Table -->
      <template #loading>
        <v-container class="ml-0"> Loading Items...Please Wait </v-container>
      </template>
      <template #[`header.data-table-select`]="{ on, props }">
        <v-simple-checkbox :ripple="false" :disabled="isEditingDisabled" v-on="on" v-bind="props" dark />
      </template>
      <template #[`item.group_manager_modified`]="{ item, on }">
        <v-simple-checkbox
          :value="item.group_manager_modified === true"
          :ripple="false"
          v-on="on"
        />
      </template>
      <template #[`item.excluded`]="{ item, on }">
        <v-tooltip :key="`excluded-${item.item_id}`" :disabled="!getExclusionReason(item)" top>
          <template #activator="{ on: tooltipOn }">
            <div class="d-inline-flex" v-on="tooltipOn">
              <v-simple-checkbox
                :value="item.excluded === '' || item.excluded === null"
                :ripple="false"
                :disabled="isEditingDisabled || isReadOnlyUser"
                v-on="on"
                @click="toggleExcluded(item)"
              />
            </div>
          </template>
          <span>{{ getExclusionReason(item) }}</span>
        </v-tooltip>
      </template>
      <template #[`item.rules_applied`]="{ item }">
        <v-tooltip top :disabled="!hasRules(item.rules_applied)">
          <template #activator="{ on }">
            <v-icon v-if="hasRules(item.rules_applied)" v-on="on" color="success">
              mdi-check-circle
            </v-icon>
          </template>
          <v-card v-if="hasRules(item.rules_applied)" width="300">
            <v-card-title class="text-subtitle-2">Applied Rules</v-card-title>
            <v-divider />
            <v-list dense>
              <v-list-item v-for="(rule, index) in parseRules(item.rules_applied)" :key="index">
                <v-list-item-content class="pl-4">
                  <v-list-item-title>Rule: {{ rule.displayLabel }}</v-list-item-title>
                  <v-list-item-subtitle>Level: {{ getRuleDisplay(rule.level) }}</v-list-item-subtitle>
                  <v-list-item-subtitle v-if="rule.priority != 52">Value: {{ rule.displayValue }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card>
        </v-tooltip>
      </template>
      <template #[`item.enforce_compliance`]="{ item }">
        <v-tooltip :key="`compliance-${item.item_id}`" :disabled="!item.enforce_compliance" top>
          <template #activator="{ on }">
            <v-icon v-on="on" :color="item.enforce_compliance ? 'green' : 'grey lighten-1'">
              mdi-shield-check-outline
            </v-icon>
            <v-icon v-if="tprProTenants && item.enforce_compliance"
              v-show="!srpIsCompliant(item)" 
              v-on="on"
              :color="getWarningColor(item)">
              mdi-exclamation-thick
            </v-icon>
          </template>
          <span>Compliance Enforced</span>
          <br>
          <span>{{ 'Variance: $' + parseFloat(item.variance).toFixed(2) }}</span>
        </v-tooltip>
      </template>
      <template #[`item.upc`]="{ item }">
        <td class="noWrap">{{ item.upc > 2 ? item.upc : '' }}</td>
      </template>
      <template #[`item.cost`]="{ item }">
        <td class="noWrap">${{ formatCurrencyDisplay(item.cost) }}</td>
      </template>
      <template #[`item.case_freight`]="{ item }">
        <td class="noWrap">${{ item.case_freight ? formatCurrencyDisplay(item.case_freight) : formatCurrencyDisplay(item.cost) }}</td>
      </template>
      <template #[`item.amap_allowance`]="{ item }">
        <td class="noWrap">
          ${{ formatCurrencyDisplay(item.amap_allowance) }}
        </td>
      </template>
      <template #[`item.dsd_oi_allowance`]="{ item }">
        <td class="noWrap">
          ${{ formatCurrencyDisplay(item.dsd_oi_allowance) }}
        </td>
      </template>
      <template #[`item.early_buy_allowance`]="{ item }">
        <td class="noWrap">
          ${{ formatCurrencyDisplay(item.early_buy_allowance) }}
        </td>
      </template>
      <template #[`item.tpr_billback_allowance`]="{ item }">
        <td class="noWrap">
          ${{ formatCurrencyDisplay(item.tpr_billback_allowance) }}
        </td>
      </template>
      <template #[`item.tpr_scan_allowance`]="{ item }">
        <td class="noWrap">
          ${{ formatCurrencyDisplay(item.tpr_scan_allowance) }}
        </td>
      </template>
      <template #[`item.tpr_scan_epay_allowance`]="{ item }">
        <td class="noWrap">
          <div class="d-flex align-center" style="white-space: nowrap;">
            <span v-if="item.tpr_scan_epay_allowance !== 'N/A'">${{ formatCurrencyDisplay(item.tpr_scan_epay_allowance) }}</span>
            <span v-else>N/A</span>
            <v-tooltip 
              v-if="item.tpr_scan_epay_allowance && item.tpr_scan_epay_allowance !== 'N/A' && item.tpr_scan_epay_allowance > 0 && $auth.tenant === 'alliance-retail-group'"
              top
            >
              <template #activator="{ on }">
                <v-icon 
                  v-on="on" 
                  color="blue" 
                  small 
                  class="ml-1"
                  @mouseenter="loadPromotionData(item, 'tpr_scan_epay')"
                >
                  mdi-information
                </v-icon>
              </template>
              <div>
                <span class="font-weight-bold">TPR Scan ePay</span>
                <br>
                <span v-if="loadingPromoPrice && selectedPromotionCategory === 'tpr_scan_epay' && selectedPromotionItem === item">Loading promotion data...</span>
                <span v-else-if="promoPromotionPriceData && promoPromotionPriceData.promotion_price && selectedPromotionCategory === 'tpr_scan_epay' && selectedPromotionItem === item">
                  Promotion Price: ${{ formatCurrencyDisplay(promoPromotionPriceData.promotion_price) }}
                </span>
                <span v-else-if="selectedPromotionCategory === 'tpr_scan_epay' && selectedPromotionItem === item">No promotion price found</span>
                <span v-else>Hover to load promotion data</span>
              </div>
            </v-tooltip>
          </div>
        </td>
      </template>
      <template #[`item.tpr_scan_compete_allowance`]="{ item }">
        <td class="noWrap">
          <div class="d-flex align-center" style="white-space: nowrap;">
            <span v-if="item.tpr_scan_compete_allowance !== 'N/A'">${{ formatCurrencyDisplay(item.tpr_scan_compete_allowance) }}</span>
            <span v-else>N/A</span>
            <v-tooltip 
              v-if="item.tpr_scan_compete_allowance && item.tpr_scan_compete_allowance !== 'N/A' && item.tpr_scan_compete_allowance > 0 && $auth.tenant === 'alliance-retail-group'"
              top
            >
              <template #activator="{ on }">
                <v-icon 
                  v-on="on" 
                  color="blue" 
                  small 
                  class="ml-1"
                  @mouseenter="loadPromotionData(item, 'tpr_scan_compete')"
                >
                  mdi-information
                </v-icon>
              </template>
              <div>
                <span class="font-weight-bold">TPR Scan Compete</span>
                <br>
                <span v-if="loadingPromoPrice && selectedPromotionCategory === 'tpr_scan_compete' && selectedPromotionItem === item">Loading promotion data...</span>
                <span v-else-if="promoPromotionPriceData && promoPromotionPriceData.promotion_price && selectedPromotionCategory === 'tpr_scan_compete' && selectedPromotionItem === item">
                  Promotion Price: ${{ formatCurrencyDisplay(promoPromotionPriceData.promotion_price) }}
                </span>
                <span v-else-if="selectedPromotionCategory === 'tpr_scan_compete' && selectedPromotionItem === item">No promotion price found</span>
                <span v-else>Hover to load promotion data</span>
              </div>
            </v-tooltip>
          </div>
        </td>
      </template>
      <template #[`item.edlc_billback_allowance`]="{ item }">
        <td class="noWrap">
          ${{ formatCurrencyDisplay(item.edlc_billback_allowance) }}
        </td>
      </template>
      <template #[`item.edlc_scan_allowance`]="{ item }">
        <td class="noWrap">
          ${{ formatCurrencyDisplay(item.edlc_scan_allowance) }}
        </td>
      </template>
      <template #[`item.edlc_scan_epay_allowance`]="{ item }">
        <td class="noWrap">
          <div class="d-flex align-center" style="white-space: nowrap;">
            <span v-if="item.edlc_scan_epay_allowance !== 'N/A'">${{ formatCurrencyDisplay(item.edlc_scan_epay_allowance) }}</span>
            <span v-else>N/A</span>
            <v-tooltip 
              v-if="item.edlc_scan_epay_allowance && item.edlc_scan_epay_allowance !== 'N/A' && item.edlc_scan_epay_allowance > 0 && $auth.tenant === 'alliance-retail-group'"
              top>
              <template #activator="{ on }">
                <v-icon 
                  v-on="on" 
                  color="blue" 
                  small 
                  class="ml-1"
                  @mouseenter="loadPromotionData(item, 'edlc_scan_epay')">
                  mdi-information
                </v-icon>
              </template>
              <div>
                <span class="font-weight-bold">EDLC Scan ePay</span>
                <br>
                <span v-if="loadingPromoPrice && selectedPromotionCategory === 'edlc_scan_epay' && selectedPromotionItem === item">Loading promotion data...</span>
                <span v-else-if="promoPromotionPriceData && promoPromotionPriceData.promotion_price && selectedPromotionCategory === 'edlc_scan_epay' && selectedPromotionItem === item">
                  Promotion Price: ${{ formatCurrencyDisplay(promoPromotionPriceData.promotion_price) }}
                </span>
                <span v-else-if="selectedPromotionCategory === 'edlc_scan_epay' && selectedPromotionItem === item">No promotion price found</span>
                <span v-else>Hover to load promotion data</span>
              </div>
            </v-tooltip>
          </div>
        </td>
      </template>
      <template #[`item.edlc_scan_compete_allowance`]="{ item }">
        <td class="noWrap">
          <div class="d-flex align-center" style="white-space: nowrap;">
            <span v-if="item.edlc_scan_compete_allowance !== 'N/A'">${{ formatCurrencyDisplay(item.edlc_scan_compete_allowance) }}</span>
            <span v-else>N/A</span>
            <v-tooltip 
              v-if="item.edlc_scan_compete_allowance && item.edlc_scan_compete_allowance !== 'N/A' && item.edlc_scan_compete_allowance > 0 && $auth.tenant === 'alliance-retail-group'"
              top>
              <template #activator="{ on }">
                <v-icon 
                  v-on="on" 
                  color="blue" 
                  small 
                  class="ml-1"
                  @mouseenter="loadPromotionData(item, 'edlc_scan_compete')">
                  mdi-information
                </v-icon>
              </template>
              <div>
                <span class="font-weight-bold">EDLC Scan Compete</span>
                <br>
                <span v-if="loadingPromoPrice && selectedPromotionCategory === 'edlc_scan_compete' && selectedPromotionItem === item">Loading promotion data...</span>
                <span v-else-if="promoPromotionPriceData && promoPromotionPriceData.promotion_price && selectedPromotionCategory === 'edlc_scan_compete' && selectedPromotionItem === item">
                  Promotion Price: ${{ formatCurrencyDisplay(promoPromotionPriceData.promotion_price) }}
                </span>
                <span v-else-if="selectedPromotionCategory === 'edlc_scan_compete' && selectedPromotionItem === item">No promotion price found</span>
                <span v-else>Hover to load promotion data</span>
              </div>
            </v-tooltip>
          </div>
        </td>
      </template>
      <template #[`item.unit_cost`]="{ item }">
        <td class="noWrap">${{ formatCurrencyDisplay(item.unit_cost) }}</td>
      </template>
      <template #[`item.net_unit_cost`]="{ item }">
        <td class="noWrap">${{ formatCurrencyDisplay(item.net_unit_cost) }}</td>
      </template>
      <template v-slot:item.gross_profit="{ item }">
        {{ item.gross_profit != null ? item.gross_profit.toFixed(2) + '%' : '' }}
      </template>
      <template #[`item.net_gross_profit`]="{ item }">
        <td class="noWrap">{{ item.net_gross_profit | format }}%</td>
      </template>
      <template #[`item.retail_price`]="{ item }">
        <td class="noWrap">${{ formatCurrencyDisplay(item.retail_price) }}</td>
      </template>
      <template #[`item.unit_savings`]="{ item }">
        <td class="noWrap">${{ calculateUnitSavings(item).toFixed(2) }}</td>
      </template>
      <template #[`item.obxi`]="{ item }">
        <td class="noWrap">{{ item.obxi }}</td>
      </template>
      <template #[`item.like_code`]="{ item }">
        <td class="noWrap">{{ item.like_code }}</td>
      </template>
      <template #[`item.linked_group`]="{ item }">
        <td class="noWrap">
          <span 
            v-if="item.linked_group && showLinkedGroupLink" 
            class="primary--text cursor-pointer text-decoration-underline"
            @click="filterByLinkedGroup(item.linked_group)"
            :title="`Filter by Linked Group: ${item.linked_group}`">
            {{ item.linked_group }}
          </span>
          <span v-else>{{ item.linked_group || '' }}</span>
        </td>
      </template>
      <template #[`item.start_dt`]="{ item }">
        <td class="noWrap">{{ formatDateDisplay(item.start_dt) }}</td>
      </template>
      <template #[`item.end_dt`]="{ item }">
        <td class="noWrap">{{ formatDateDisplay(item.end_dt) }}</td>
      </template>
      <template #[`item.store_name`]="{ item }">
        <td class="noWrap">{{ item.store_name }}</td>
      </template>
      <template #[`item.updated_on`]="{ item }">
        <td class="noWrap">{{ formatDateTimestamp(item.updated_on) }}</td>
      </template>
      <template #[`item.rules_promotion_price`]="{ item }">
        <td class="noWrap" v-if="item.rules_promotion_price">
          ${{ formatCurrencyDisplay(item.rules_promotion_price) }}
        </td>
      </template>
      <template #[`item.original_promotion_price`]="{ item }">
        <td class="noWrap" v-if="item.original_promotion_price">
          ${{ formatCurrencyDisplay(item.original_promotion_price) }}
        </td>
      </template>
      <template #[`item.promotion_price`]="{ item, index }">
        <BatchSrpCell
          :item="item"
          :index="index"
          :is-editing-disabled="isEditingDisabled"
          :srp-exceeds-base-retail="srpExceedsBaseRetail(item)"
          :tpr-pro-tenants="tprProTenants"
          :has-selections="hasSelections"
          :srp-rules="srpRules"
          :is-changed="changedItemIds.has(item.id)"
          @multiplier-change="onMultiplierChange"
          @multiplier-blur="onMultiplierBlur"
          @gp-recompute="debouncedUpdateGrossProfit"
          @handle-input="onHandleInput"
          @srp-blur="onSrpBlur"
          @srp-change="onSrpChange"
          @enter="onEnter"
          @show-linked-group="showLinkedGroupModal"
          @reset="resetRow"
        />
      </template>
      <template #[`item.total_allowance_per_unit`]="{ item }">
        <td class="noWrap">${{ Number(item.total_allowance_per_unit).toFixed(2) }}</td>
      </template>
      <template #[`item.srp_needed_to_maintain_margin`]="{ item }">
        <td class="noWrap">${{ Number(item.srp_needed_to_maintain_margin).toFixed(2) }}</td>
      </template>
      <template #[`item.difference_to_regular_price`]="{ item }">
        <td class="noWrap" :class="{ 'red--text': calculateDifferenceToRegularPrice(item) > 0 }">
          ${{ calculateDifferenceToRegularPrice(item).toFixed(2) }}
        </td>
      </template>
      <template #[`item.prev_msu`]="{ item }">
        <td class="noWrap">{{ item.prev_msu || '---' }}</td>
      </template>
      <template #[`item.prev_retail`]="{ item }">
        <td class="noWrap">{{ item.prev_retail ? `$${formatCurrencyDisplay(item.prev_retail)}` : '---' }}</td>
      </template>
      <template #[`item.prev_margin`]="{ item }">
        <td class="noWrap">{{ item.prev_margin ? `${item.prev_margin}%` : '---' }}</td>
      </template>
      <template #[`item.prev_net_unit_cost`]="{ item }">
        <td class="noWrap">{{ item.prev_net_unit_cost ? `$${formatCurrencyDisplay(item.prev_net_unit_cost)}` : '---' }}</td>
      </template>
      <template #[`item.previous_tpr_cost`]="{ item }">
        <td class="noWrap">{{ item.previous_tpr_cost ? `$${formatCurrencyDisplay(item.previous_tpr_cost)}` : '---' }}</td>
      </template>
      <template #[`item.previous_tpr_msu`]="{ item }">
        <td class="noWrap">{{ item.previous_tpr_msu || '---' }}</td>
      </template>
      <template #[`item.previous_tpr_retail`]="{ item }">
        <td class="noWrap">{{ item.previous_tpr_retail ? `$${formatCurrencyDisplay(item.previous_tpr_retail)}` : '---' }}</td>
      </template>
      <template #[`item.previous_tpr_margin`]="{ item }">
        <td class="noWrap">{{ item.previous_tpr_margin ? `${item.previous_tpr_margin}%` : '---' }}</td>
      </template>
      <template #[`item.current_tpr_msu`]="{ item }">
        <td class="noWrap">{{ item.current_tpr_msu || '---' }}</td>
      </template>
      <template #[`item.current_tpr_retail`]="{ item }">
        <td class="noWrap">{{ item.current_tpr_retail ? `$${formatCurrencyDisplay(item.current_tpr_retail)}` : '---' }}</td>
      </template>
      <template #[`item.current_tpr_end_date`]="{ item }">
        <td class="noWrap">{{ item.current_tpr_end_date ? formatDateDisplay(item.current_tpr_end_date) : '---' }}</td>
      </template>
      <template #[`item.length_of_tpr`]="{ item }">
        <td class="noWrap">{{ item.length_of_tpr ? `${item.length_of_tpr} days` : '---' }}</td>
      </template>
      <template v-for="index in compPricingModes" v-slot:[`item.comp_pricing_${index}`]="{ item }">
        <td class="noWrap" :key="index">
          <div class="d-flex align-center">
            <v-tooltip v-if="getCompetitorPrice(item, index) !== 'N/A'" bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-if="tprProTenants" v-bind="attrs" v-on="on" :color="getPriceComparisonColor(item, index)">
                  {{ getPriceComparisonIcon(item, index) }}
                </v-icon>
              </template>
              <span>{{ getPriceComparisonText(item, index) }}</span>
            </v-tooltip>
            <span class="ml-2">
              <span v-if="getCompetitorPrice(item, index) !== 'N/A'">${{ formatCurrencyDisplay(getCompetitorPrice(item, index)) }}</span>
              <span v-else>N/A</span>
            </span>
          </div>
        </td>
      </template>
      <template #[`item.promo_info`]="{ item }">
        <v-menu :close-on-content-click="false" offset-x offset-y open-on-click right>
          <template v-slot:activator="{ on: menu, attrs }">
            <v-tooltip top>
              <template v-slot:activator="{ on: tooltip }">
                <v-btn @click="getPromosByRow(item)" color="primary" icon tabindex="-1" v-bind="attrs"
                  v-on="{ ...tooltip, ...menu }">
                  <v-icon>mdi-magnify</v-icon>
                </v-btn>
              </template>
              <span>View Promos</span>
            </v-tooltip>
          </template>
          <v-card width="400">
            <v-toolbar flat class="accent" dark>
              <v-toolbar-title>Included in Allowances</v-toolbar-title>
            </v-toolbar>
            <v-divider />
            <v-container fluid class="ma-0 pa-0" style="max-height: 250px; overflow-y: auto">
              <v-list class="py-0" dense>
                <v-list-item v-for="promo in promosByRow" :key="promo.promo_id" link @click="openLink(promo.promo_id)">
                  <v-list-item-content class="primary--text">
                    <v-list-item-title>{{
                      promo.promo_name
                      }}</v-list-item-title>
                    <template v-if="promosByRow.length > 0">
                      <v-list-item-subtitle>{{ promo.promo_number }}</v-list-item-subtitle>
                      <v-list-item-subtitle v-if="$auth.tenant === 'awg'">
                        {{ formatDateDisplay(promo.promo_start_date) }} - {{ formatDateDisplay(promo.promo_end_date) }}
                      </v-list-item-subtitle>
                    </template>
                    <template v-else>
                      <v-list-item-subtitle>No Promos to Show</v-list-item-subtitle>
                    </template>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-container>
          </v-card>
        </v-menu>
      </template>
      <template v-if="tenant=='alliance-retail-group'" #[`item.is_shipper`]="{ item }">
        <span v-if="item.is_shipper">
          <v-btn icon color="primary" @click.stop="selectedItem = item; showComponents = true">
            <v-icon>mdi-view-list</v-icon>
          </v-btn>
        </span>
      </template>
      <template #item.data-table-expand="{item, isExpanded}">
        <v-icon
          v-if="item.alternate_store_items && item.alternate_store_items.length > 0"
          :color="retailMatchesAcrossStores(item) ? 'black' : 'red darken-2'"
          @click="handleExpansion(item, isExpanded)"
        >{{ isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
      </template>
      <!-- As of right now, Store users dont get a Note field -->
      <template v-if="!isTprProRetailUser" #expanded-item="{ item, headers }">
        <td :colspan="headers.length" class="grey lighten-3 pa-0">
          <v-text-field tabindex="-1" background-color="grey lighten-3" color="primary" dense flat hide-details
            placeholder="Add Note" v-model="item.note" @change="noteUpdate(item)">
            <template #prepend-inner>
              <label class="font-weight-medium mx-3 pt-1">Note:</label>
            </template>
          </v-text-field>
        </td>
      </template>
      <template v-else-if="isTprProRetailUser" #expanded-item="{ item, headers }">
        <BatchItemAlternateStores
          :headers="headers"
          :parentCompPricingModes="compPricingModes"
          :stores="selectedStoreParty.children"
          :item="item"
          :alternateStoreItems="item.alternate_store_items"
          :selectedBatch="selectedBatch"
          :batchItems="cascadeRows"
          :parentColumnWidths="columnWidths"
          :isEditingDisabled="isEditingDisabled"
          :storeGroupIsSelected="storeGroupIsSelected"
          :changedItemIds="changedItemIds"
          @linkedGroupUpdate="linkedGroupSave"
          @alternateItemsUpdate="alternateItemsUpdate"
          @refreshItems="markBatchDataChanged(); getItems()"
          @subItemChange="handleSubItemChange"
          @subItemReset="handleSubItemReset"
          @subItemsSaved="handleSubItemsSaved">
        </BatchItemAlternateStores>
      </template>
      <template #footer>
        <v-divider></v-divider>
        <v-toolbar flat color="#fff" style="margin-top: 16px; height: 56px;">
          <v-row class="align-center" style="height: 100%;">
            <v-spacer />
            <v-col cols="2" class="d-flex align-center py-0" style="max-width: 150px;">
              <v-select :items="pageSizes" :menu-props="{
                top: true,
                offsetY: true,
                maxHeight: 500
                }" dense height="32" label="Items Per Page" v-model="pageSize">
              </v-select>
            </v-col>
            <v-col cols="auto" class="d-flex align-center py-0" style="margin-top: -26px;">
            <v-btn :disabled="disablePrevious || loading" @click="previous" class="mx-2" icon large>
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <small>Page {{ page }}</small>
            <v-btn :disabled="disableNext || loading" @click="next" class="mx-2" icon large>
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-toolbar>
    </template>
    </v-data-table>
    </div>
    <v-expand-x-transition>
      <SearchFilters
        v-show="filterDrawer"
        ref="searchFilters"
        v-model="searchParams"
        :categories="categoryList"
        :subcategories="subcategoryList"
        :departments="departmentList"
        :promoCategories="promoCategoryList"
        :vendors="vendorList"
        :batch_type="selectedBatch.batch_type"
        :panel-height="drawerHeight || tableSize"
        class="pp-filter-drawer flex-shrink-0"
        @input="getItems"
        @filters-cleared="onFiltersCleared"
        @close="filterDrawer = false" />
    </v-expand-x-transition>
   </div>
    <v-dialog v-model="showConfirmPopup" persistent width="400">
      <v-card class="modalCard" height="220">
        <v-toolbar flat dark class="main">
          <v-toolbar-title>Confirm SRP Values</v-toolbar-title>
        </v-toolbar>
        <v-card-text>
          <!-- Single Column Wrapping All Rows -->
          <v-col>
            <!-- Checkbox Section -->
            <v-row class="align-center">
              <v-checkbox
                id="checkbox"
                v-model="confirmChecked"
                label="I understand that I am saving items with an SRP value that falls outside of the compliance range."
                hide-details
              ></v-checkbox>
            </v-row>
          </v-col>
        </v-card-text>
        <v-card-actions>
          <v-row justify="center">
            <v-col cols="auto">
              <v-btn @click="cancelConfirm">Cancel</v-btn>
              <v-btn
                class="ml-3 green white--text"
                :disabled="!confirmChecked"
                @click="saveChanges"
              >
              Confirm Save
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <StoreGroupModal
      :value.sync="storeGroupModal"
      :userStores="userStores"
      :storeParty="selectedStoreParty">
    </StoreGroupModal>
    <AlertDialog :value.sync="alertDialog" :batch="selectedBatch" :total="total" @update="batchUpdate($event)" />
    <AdGroupShuttle :value.sync="adShuttleDialog" :ad_group="selectedAdGroup" :batch="selectedBatch" :batch_changes="changesAvailableForShuttle" :store_ids="storeGroupIsSelected ? selectedStoreParty.children.flatMap(p => p.store_party_id) : [selectedStore.store_party_id]"/>
    <LinkedGroupModal 
      v-if="linkedGroupModal"
      :value.sync="linkedGroupModal" 
      :item="linkedGroupItem"
      :batch="selectedBatch" 
      :batchItems="cascadeRows"
      :store="linkedGroupStore || primaryStore"
      :isTprProRetailUser="isTprProRetailUser"
      :storeGroupIsSelected="storeGroupIsSelected"
      :propagateChangesToChildRows="propagateChangesToChildRows"
      @onSave="linkedGroupSave"
      @onIncluded="markBatchDataChanged(); getItems()">
    </LinkedGroupModal>
    <AIInsightsModal
      :value.sync="aiInsightsDialog"
      :fetcher="fetchAiInsights"
      :cache-key="aiInsightsCacheKey"
      :report-subtitle="aiInsightsSubtitle"
      :pdf-name="selectedBatch.batch_name || ''"
      :show-cost="adminUser"
      @generated="onAiInsightsGenerated"
      @exported="onAiInsightsExported"
      @export-failed="alert('Could not create the PDF. Please try again.')" />

    <v-overlay absolute :value="exporting">
      <v-progress-circular indeterminate size="100">
        Exporting...
      </v-progress-circular>
    </v-overlay>
    <ComponentItems
      v-if="showComponents"
      v-model="showComponents"
      @close="showComponents = false"
      :selectedItem="selectedItem"
      :selectedBatch="selectedBatch"
    />
  </v-container>
</template>
<script>
import { cloneDeep, debounce } from 'lodash'
import Items from '@/axios/items'
import Rules from '@/axios/rules-management.js'
import { retryApiCall } from '@/store/index.js'
import { fullwidth } from '@/mixins/fullwidth'
import { paging } from '@/mixins/paging'
import { displayHelpers } from '@/mixins/displayHelpers'
import { userAccess } from '@/mixins/user-access'
import { utils } from '@/mixins/utils'
import { shared } from '@/mixins/shared'
import { itemCache } from '@/mixins/itemCache'
import { tprItemRow } from '@/mixins/tprItemRow'
import StoreGroupModal from '@/components/StoreGroupModal.vue'
import SearchFilters from '@/components/SearchFilters.vue'
import ComponentItems from '@/components/ComponentItems.vue'
import Draggable from 'vuedraggable'
import AdGroupShuttle from '@/components/AdGroupShuttle.vue'
import LinkedGroupModal from '@/components/LinkedGroupModal.vue'
import BatchItemAlternateStores from '@/components/BatchItemAlternateStores.vue'
import BatchSrpCell from '@/components/BatchSrpCell.vue'
import AIInsightsModal from '@/components/AIInsightsModal.vue'
import { selectDisplayPrice, hasCompetitiveRuleApplied } from '@/utils/competitiveRules'
import { attachStoreRows } from '@/utils/storeRows'
export default {
  name: 'BatchView',
  components: { SearchFilters, ComponentItems, Draggable, LinkedGroupModal, BatchItemAlternateStores, StoreGroupModal, BatchSrpCell, AIInsightsModal },
  mixins: [fullwidth, displayHelpers, paging, userAccess, utils, shared, AdGroupShuttle, itemCache, tprItemRow],
  data() {
    return {
      actionMenu: false,
      infoMenu: false,
      sessionMenu: false,
      storeGroupModal: false,
      adShuttleDialog: false,
      selectedAdGroup: {},
      categoryList: [],
      subcategoryList: [],
      saveMethodToggle: 0,
      confirmChecked: false,
      changesAvailableForShuttle: [],
      selectedStore: {},
      selectedStoreParty: {},
      deleteMenu: false,
      departmentList: [],
      promoCategoryList: [],
      vendorList: [],
      expanded: [],
      items: [],
      fullMultiStoreItems: [],
      fullClientItems: [],
      lastClientFetchSig: null,
      linkedGroupModal: false,
      aiInsightsDialog: false,
      // Bumped after every write, so cached AI Insights never describe data from before a save.
      batchDataVersion: 0,
      loading: true,
      multiplier: '',
      displayHeaders: [],
      hideHeaders: [],
      search: '',
      filterDrawer: false,
      drawerHeight: 0,
      searchMenu: false,
      searchValues: [],
      searchParams: {},
      selectedRow: {},
      selected: [],
      sortBy: [],
      sort: null,
      showConfirmPopup: false,
      showRemoveSortsButton: false,
      showModal: false,
      showComponents: false,
      selectedHeaders: [],
      selectedItem: {},
      srp: '',
      srprefresh: 0,
      srpMenu: false,
      tprDate: '',
      tprEndDate: '',
      changes: [],
      originalItems: [],
      banners: [],
      selectedPromotionItem: null,
      selectedPromotionCategory: '',
      selectedPromotionAllowance: 0,
      promoPromotionPriceData: null,
      loadingPromoPrice: false,
      savedStateBeforeFilters: null,
      isRestoringState: false,
      truncateAt: 25,
      showExpanded: false,
      requestSequence: 0,
      activeRequestId: 0,
      isSessionRestoring: false,
      linkedGroupItem: null,
      linkedGroupStore: null,
      columnWidths: [],
      lastFetchedPageKeys: [],
      lastFetchedPage: null,
      lastFetchWasFinalPage: false
    }
  },
  beforeRouteLeave(to, from, next) {
    if (this.hasActiveChanges) {
      if (confirm('You have unsaved changes. Do you want to discard changes?')) {
        this.changes = []
        localStorage.removeItem(`changes_${this.selectedBatch.id}`)
        next()
      } else {
        next(false)
      }
    } else {
      next()
    }
  },
  created() {
      this.nonTableHeight = 240
      this.loadUserHeaders()
      this.initializeBatchView()
      const userSaveMethod = sessionStorage.getItem('save_method_toggle')
      if (userSaveMethod) this.saveMethodToggle = Number(userSaveMethod)
      this.debouncedUpdateGrossProfit = debounce(item => this.updateGrossProfit(item), 500)
      this.debouncedPersistChanges = debounce(changes => {
        if (this.selectedBatch?.id) {
          localStorage.setItem(`changes_${this.selectedBatch.id}`, JSON.stringify(changes))
        }
      }, 500)
  },
  mounted() {
    this.loadUniqueValues()
    window.addEventListener('beforeunload', this.handleBeforeUnload)
    window.addEventListener('resize', this.updateColumnWidths)
    window.addEventListener('resize', this.updateDrawerHeight)
    this.$root.$on("shuttleWholeBatch", this.handleShuttleWholeBatch);
    this.$nextTick(() => {
      this.updateColumnWidths()
      this.updateDrawerHeight()
    })
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateColumnWidths)
    window.removeEventListener('resize', this.updateDrawerHeight)
  },
  watch: {
    'selectedBatch.status'(newStatus) {
      if (newStatus === 'PUBLISHED') {
        this.alert('This batch is published and cannot be modified.')
      }
    },
    'selectedStore.is_price_pro_store'(newValue) {
      if (this.isTprProRetailUser && newValue === false) {
        this.alert('This store is not a Price Pro store and cannot be modified.')
      }
    },
    storeGroupIsSelected() {
      // created() runs before the store party is known, so this fires when the real mode
      // settles as well as on a later store <-> group switch. Swap to that mode's layout.
      this.loadUserHeaders()
    },
    allHeaders(newHeaders, oldHeaders) {
      // allHeaders grows after mount - the comp_pricing_* banner columns are appended once
      // getItems() has populated this.banners. Re-read the saved layout now that the header set
      // is complete, so columns filtered out as unknown on the first pass come back in their
      // stored positions. Skipped while the ordering dialog is open so an in-progress drag
      // isn't reset under the user.
      if (this.showModal) return
      // The computed rebuilds its array on every evaluation, so this also fires for dep changes
      // that leave the columns alone - an Auth0 token refresh reaches it via isTprProRetailUser.
      // Only the header identity matters here.
      const sameColumns = oldHeaders
        && newHeaders.length === oldHeaders.length
        && newHeaders.every((h, i) => h.value === oldHeaders[i].value)
      if (sameColumns) return
      this.loadUserHeaders()
      // syncHeadersOrder rebuilds displayHeaders from fresh header objects, which drops the
      // sort arrows, so re-apply them from sortBy.
      this.updateHeaderSortIndicators()
    },
    srpMenu: {
      handler(newValue) {
        if (newValue === false) {
          this.$refs.form.reset()
        }
      }
    },
    searchMenu: {
      handler(newValue) {
        if (newValue === false) {
          this.$refs.form.reset()
        }
      }
    },
    searchValues: {
      handler(newValue, oldValue) {
        sessionStorage.setItem(
          'tpr_manager_item_search',
          JSON.stringify(newValue)
        )
        if (this.isSessionRestoring) {
          return
        }
        if (newValue.length !== oldValue.length) {
          this.page = 1
          this.loadItems()
        }
      }
    },
    linkedGroupModal(newValue) {
      if (!newValue) {
        this.linkedGroupStore = null
        this.linkedGroupItem = null
      }
    },
    searchParams: {
      handler (newValue, oldValue) {
        if (this.isSessionRestoring) {
          return
        }
        if (newValue) {
          // Save state before applying filters (only if no filters were previously applied)
          const hadFilters = oldValue && Object.keys(oldValue).some(key => {
            const value = oldValue[key]
            return Array.isArray(value) ? value.length > 0 : value !== null && value !== undefined && value !== ''
          })
          const hasFilters = this.hasFiltersApplied()
          // Save state before first filter is applied
          if (!hadFilters && hasFilters) {
            this.saveStateBeforeFilters()
          }
          // Restore state when all filters are removed
          if (hadFilters && !hasFilters) {
            this.isRestoringState = true
            this.restoreStateAfterFiltersRemoved()
            return // Skip the page reset below during restoration
          }
          // Don't reset page to 1 if we're in the middle of restoring state
          if (this.page > 1 && !this.isRestoringState) {
            this.page = 1
          }
        }
      },
      deep: true
    },
    saveMethodToggle: {
      handler(newValue) {
        sessionStorage.setItem('save_method_toggle', Number(newValue))
      },
      deep: true
    },
    sortBy(newVal) {
      this.showRemoveSortsButton = newVal.length > 0 // Show button if sortBy length is greater than 1
    },
    filterDrawer(open) {
      if (open) this.updateDrawerHeight()
    },
    changes: {
      handler(newValue) {
        if (newValue) {
          this.debouncedPersistChanges(newValue)
        }
      },
      deep: true,
    },
    page: {
      handler(newValue) {
        this.onPageChanged(newValue)
      }
    },
    pageSize() {
      if (this.isTprProRetailUser && this.page === 1) {
        this.sliceCurrentPage()
      }
    },
    displayHeaders: {
      handler() {
        this.$nextTick(() => this.updateColumnWidths())
      },
      deep: true
    },
    selectedHeaders: {
      handler() {
        this.$nextTick(() => this.updateColumnWidths())
      },
      deep: true
    }
  },
  computed: {
    allExpanded() {
      const expandable = this.items.filter(i => i.alternate_store_items && i.alternate_store_items.length > 0)
      return expandable.length > 0 && expandable.every(i => this.expanded.includes(i))
    },
    primaryStore() {
      if (this.storeGroupIsSelected) {
        const storeGroup = cloneDeep(this.selectedStoreParty)
        const sorted = storeGroup.children.sort(this.TPRStoreGroupPrimarySort)
        const primary = {
          ...sorted[0],
          store_party_id: sorted[0].store_party_id
        }
        return primary
      } else return this.selectedStore
    },
    propagateChangesToChildRows() {
      return this.saveMethodToggle === 0
    },
    isPublished() {
      return this.selectedBatch.status === 'PUBLISHED'
    },
    isEditingDisabled() {
      if (this.isReadOnlyAdmin) return true
      // Disable editing if batch is published
      if (this.isPublished) return true
      // Disable editing for TPR Pro retail users viewing non-Price Pro stores
      if (this.isTprProRetailUser && this.selectedStore && this.primaryStore.is_price_pro_store === false) return true
      return false
    },
    // Every row the user could have edited, keyed by id. `items` is only the rendered page, so
    // anything that validates a change or builds a save payload must resolve through here.
    // Resolving through `items` makes a row the user paged away from fall back to a pre-edit
    // snapshot, and the guardrails then score the old price instead of the typed one.
    cascadeRows() {
      if (this.isTprProRetailUser && this.fullMultiStoreItems.length) return this.fullMultiStoreItems
      if (this.fullClientItems.length) return this.fullClientItems
      return this.items
    },
    itemsById() {
      const map = new Map()
      const sources = [this.items, this.cascadeRows]
      for (const source of sources) {
        for (const i of source) if (!map.has(i.id)) map.set(i.id, i)
      }
      // Cascaded store rows next. `items` is a slice() of cascadeRows, so those two hold the
      // same objects and their order between themselves does not matter; what matters is that
      // both come before the originalItems snapshots, which are frozen pre-edit copies.
      for (const source of sources) {
        for (const i of source) {
          if (!i.alternate_store_items?.length) continue
          for (const asi of i.alternate_store_items) if (!map.has(asi.id)) map.set(asi.id, asi)
        }
      }
      // Pre-edit snapshots are a last resort, for rows no longer held in memory at all.
      for (const i of this.originalItems) if (!map.has(i.id)) map.set(i.id, i)
      return map
    },
    // What propagating this edit would do to the other stores, keyed by the parent row that
    // caused it. Two separate rules are in play and the message has to name the right one:
    // the mandatory pass-through ceiling, and the enforced-compliance tolerance around the
    // original promo price. Neither was ever applied to a propagated row -- the cascade
    // payloads are built at save time, after every gate in this file has already run, so a
    // focal price that is fine at the focal store could push a child store out of either
    // rule with nothing to stop it. Stores in a group can hold base retails several dollars
    // apart, so this is routine rather than an edge case.
    //
    // Side-effect free: the Save gate, the tooltip and the blur warning all read it.
    propagatedBreaches() {
      const breaches = new Map()
      // storeGroupIsSelected already implies isTprProRetailUser, via useStoreParties
      if (!this.propagateChangesToChildRows || !this.storeGroupIsSelected) return breaches
      for (const change of this.changes) {
        const parent = this.effectiveRow(change)
        if (!parent || !parent.alternate_store_items?.length) continue
        const offenders = []
        for (const asi of parent.alternate_store_items) {
          if (this.rowIsExcluded(asi)) continue
          // score the child against its own store's limits, carrying the parent's new price
          const projected = {
            ...asi,
            promotion_price: parent.promotion_price,
            multiplier: parent.multiplier
          }
          // Both rules go through the one ceiling: a compliance-enforced child is held to its
          // required retail, any other child to its pass-through (see passPercentMaxSRP).
          // srpIsCompliant is not consulted -- it only blocks behind blockNonCompliantSaves,
          // which is `tprProTenants && !isTprProRetailUser`, and a store group implies a
          // retail user.
          if (!this.passPercentIsCompliant(projected)) offenders.push({ row: asi })
        }
        if (offenders.length) breaches.set(change.id, offenders)
      }
      return breaches
    },
    // Rows that propagation would push above their own base retail, keyed by the parent whose
    // edit caused it. Separate from propagatedBreaches on purpose: this does not block a save.
    // The write is legitimate and an excluded row keeps its exclusion either way. What it does
    // do is strand the row -- toggleExcluded refuses to include a row whose SRP exceeds base
    // retail, so a propagated price can leave one un-includable until someone lowers it by
    // hand. The cascaded table already warns when you blur a child field; this covers the
    // parent-row edit, which is how it actually happens and had no warning at all.
    propagatedBaseRetailWarnings() {
      const warnings = new Map()
      if (!this.propagateChangesToChildRows || !this.storeGroupIsSelected) return warnings
      for (const change of this.changes) {
        const parent = this.effectiveRow(change)
        if (!parent || !parent.alternate_store_items?.length) continue
        const offenders = parent.alternate_store_items.filter(asi => this.srpExceedsBaseRetail({
          ...asi,
          promotion_price: parent.promotion_price,
          multiplier: parent.multiplier
        }))
        if (offenders.length) warnings.set(change.id, offenders)
      }
      return warnings
    },
    // Ids of pending changes that cannot be saved, so the Save button can name what blocks it.
    invalidChangeIds() {
      const ids = []
      for (const change of this.changes) {
        const item = this.effectiveRow(change)
        if (!item) continue
        const invalid = item.promotion_price <= 0 ||
          item.multiplier <= 0 ||
          (this.isTprProRetailUser && !this.passPercentIsCompliant(item)) ||
          (this.blockNonCompliantSaves && !this.srpIsCompliant(item))
        if (invalid) ids.push(change.id)
      }
      // A row whose own values are fine is still blocked by what it would propagate.
      for (const id of this.propagatedBreaches.keys()) {
        if (!ids.includes(id)) ids.push(id)
      }
      return ids
    },
    hasInvalidInputs() {
      return this.invalidChangeIds.length > 0
    },
    // A greyed-out Save with no explanation is the complaint behind this; name the rows.
    saveBlockedReason() {
      if (!this.invalidChangeIds.length) return ''
      const labels = this.invalidChangeIds.map(id => this.itemsById.get(id)?.item_id || id)
      const shown = labels.slice(0, 5).join(', ')
      const more = labels.length > 5 ? ` and ${labels.length - 5} more` : ''
      const propagated = this.propagatedBreaches.size
        ? '. Expand those rows to see the other stores affected'
        : ''
      return `Cannot save - check the SRP and multiplier on: ${shown}${more}${propagated}`
    },
    hasActiveChanges() {
      return this.changes.length > 0
    },
    changedItemIds() {
      return new Set(this.changes.map(c => c.id))
    },
    primaryStoreIsPricePro() {
      const flag = this.primaryStore?.is_price_pro_store
      return typeof flag === 'string' ? flag.toLowerCase() === 'true' : Boolean(flag)
    },
    // ARG only: AI Insights is part of Price Pro. There, admins and ad-group users always; store
    // users only on a Price Pro store (the group's primary store for a store group). The API
    // enforces the same rules.
    showAiInsightsButton() {
      if (!this.tprProTenants) return false
      if (this.adminUser || !this.isTprProRetailUser) return true
      return this.primaryStoreIsPricePro
    },
    aiInsightsLevel() {
      return this.isTprProRetailUser ? 'store' : 'ad_group'
    },
    aiInsightsBatchId() {
      return this.isTprProRetailUser ? this.selectedBatch?.tpr_batch_id : this.selectedBatch?.id
    },
    aiInsightsStoreIds() {
      if (!this.isTprProRetailUser) return []
      const storeIds = this.storeGroupIsSelected
        ? (this.selectedStoreParty.children || []).map(p => p.store_party_id)
        : [this.selectedStore?.store_party_id]
      return storeIds.filter(Boolean)
    },
    aiInsightsBlockedReason() {
      if (this.isSyncing) return 'Wait for the batch sync to finish'
      if (this.hasActiveChanges) return 'Save or reset your changes to generate insights'
      if (!this.aiInsightsBatchId) return 'Batch is still loading'
      if (this.isTprProRetailUser && !this.aiInsightsStoreIds.length) return 'Select a store to generate insights'
      return ''
    },
    aiInsightsCacheKey() {
      return [this.aiInsightsBatchId, this.aiInsightsLevel, this.aiInsightsStoreIds.join(','), this.batchDataVersion].join('|')
    },
    aiInsightsSubtitle() {
      const batch = this.selectedBatch || {}
      const parts = []
      if (batch.batch_name) parts.push(batch.batch_name)
      if (batch.tpr_date) parts.push(`TPR ${batch.tpr_date}${batch.tpr_end_date ? ` to ${batch.tpr_end_date}` : ''}`)
      if (this.isTprProRetailUser) {
        const storeName = this.storeGroupIsSelected ? this.selectedStoreParty?.store_name : this.selectedStore?.store_name
        if (storeName) parts.push(storeName)
      }
      return parts.join(' | ')
    },
    tprProTenants() {
      return this.$auth.tenant === 'alliance-retail-group'
    },
    isArgOrAwgTenant() {
      return this.$auth.tenant === 'awg' || this.$auth.tenant === 'alliance-retail-group'
    },
    userEmail() {
      return this.$auth.user.email
    },
    promosByRow() {
      return this.selectedRow ? this.selectedRow.promos : []
    },
    isFiltered() {
      return this.searchValues.length > 0 ? this.searchValues.length > 0 : false
    },
    activeFilterCount() {
      if (!this.searchParams) return 0
      return Object.keys(this.searchParams).filter(key => {
        const v = this.searchParams[key]
        if (v === null || v === undefined || v === '') return false
        return Array.isArray(v) ? v.length > 0 : true
      }).length
    },
    hasSelections() {
      return this.selected.length > 0
    },
    breadcrumbs() {
      const tprDateFormatted = this.moment(this.tprDate).format(
        this.$dateConfig.date_display
      )
      return [
        {
          text: this.selectedAdGroup.name || (this.storeGroupIsSelected ? this.selectedStoreParty.store_name : this.selectedStore.store_name),
          exact: true,
          href: '/',
        },
        {
          text: this.selectedBatch?.batch_name
            ? `${this.formatName(this.selectedBatch.batch_name)} (${tprDateFormatted})`
            : tprDateFormatted,
          expanded_text: this.selectedBatch?.batch_name
            ? `${this.selectedBatch.batch_name} (${tprDateFormatted})`
            : tprDateFormatted,
          exact: true,
          disabled: true,
        }
      ]
    },
    getBannerPrices() {
      return (item) => {
        if (!item.comp_pricing) return {}
        try {
          const parsedPricing = JSON.parse(item.comp_pricing)
          return parsedPricing.reduce((acc, banner) => {
            acc[banner.banner] = banner.regular_price_mode || 0
            return acc
          }, {})
        } catch (e) {
          console.error('Failed to parse comp_pricing:', e)
          return {}
        }
      }
    },
    disablePrevious() {
      return (this.page - 1) * this.pageSize === 0
    },
    disableNext() {
      if (this.isTprProRetailUser) {
        return this.page * this.pageSize >= this.fullMultiStoreItems.length
      }
      // sorting a client-computed column paginates the full set in the browser, so bound "next" by the full set length
      if (this.hasClientSort) {
        return this.page * this.pageSize >= this.fullClientItems.length
      }
      return this.lastFetchWasFinalPage || this.items.length < this.pageSize
    },
    hasClientSort() {
      return this.sortBy.some(([column]) => this.isClientSortColumn(column))
    },
    isPercentageProofStoreGroup() {
      return !this.selectedStoreParty?.children?.some(store => store.attributes?.STORE_PRICE_PREFERENCE !== 'PERCENTAGE_PROOF')
    },
    storeGroupIsSelected() {
      return this.useStoreParties && this.selectedStoreParty?.party_type === 'TPR_STORE_GROUP'
    },
    headerLayoutMode() {
      return this.storeGroupIsSelected ? 'group' : 'store'
    },
    useStoreParties() {
      return this.isTprProRetailUser && this.$auth.tenant === 'alliance-retail-group'
    },
    hasWarnings() {
      return this.toolbarWarnings.length > 0
    },
    toolbarWarnings() {
      let warnings = []
      if (this.hasActiveChanges) {
        warnings.push({type: "warning", message: "Warning: Please save changes before leaving Batch View."})
      }
      if (this.isEditingDisabled && this.isTprProRetailUser) {
        if (!this.storeGroupIsSelected && this.selectedStore && this.selectedStore.is_price_pro_store === false) {
          warnings.push({type: "info", message: "Info: This store is not a Price Pro store and cannot be modified."})
        } else if ((this.storeGroupIsSelected) && this.primaryStore && this.primaryStore.is_price_pro_store === false) {
          warnings.push({type: "info", message: "Info: The primary store in the store group is not a Price Pro store and cannot be modified."})
        }
      }
      if (this.isPublished) warnings.push({type: "info", message: "Info: This batch is published and cannot be modified."})
      return warnings
    },
    toolbarWarningsColor() {
      if (this.toolbarWarnings.length == 0) return ''
      if (this.toolbarWarnings.some(alert => alert.type === 'warning')) return "orange"
      else return "blue"
    },
    showLinkedGroupLink() {
      return [].includes(this.$auth.tenant)
    },
    allHeaders() {
      let headers
      if (this.$auth.tenant === 'alliance-retail-group') {
        headers = [
          { sortable: false, class: 'accent', value: 'data-table-select', text: 'Checkbox', default_show: true },
          { text: 'Store', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'store_name', default_show: true, show: this.storeGroupIsSelected },
          { text: 'Included', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'excluded', default_show: true },
          { text: 'Compliance Enforced', align: 'center', sortable: true, class: 'accent white--text', value: 'enforce_compliance', default_show: true },
          { text: 'Item ID', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'item_id', default_show: true },
          { text: 'Description', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'description', default_show: true },
          { text: 'UPC', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'upc', default_show: true },
          { text: 'Pack', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'qty_per_case', default_show: true },
          { text: 'Size', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'pack_size', default_show: true },
          { text: 'Department', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'department', default_show: true },
          { text: 'Case Cost/FRT', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'case_freight', default_show: true },
          { text: 'TPR Scan', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'tpr_scan_allowance', default_show: true },
          { text: 'TPR Scan ePay', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'tpr_scan_epay_allowance', default_show: true },
          { text: 'TPR Scan Compete', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'tpr_scan_compete_allowance', default_show: true },
          { text: 'AMAP', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'amap_allowance', default_show: true },
          { text: 'DSD OI', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'dsd_oi_allowance' },
          { text: 'TPR Billback', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'tpr_billback_allowance', default_show: true },
          { text: 'Reg Unit Cost/FRT', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'unit_cost', default_show: true },
          { text: 'Reg Retail', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'retail_price', default_show: true },
          { text: 'Unit Savings', align: 'start', sortable: true, filterable: false, class: 'accent white--text', value: 'unit_savings', default_show: false },
          { text: 'Reg GP%', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'gross_profit', default_show: true },
          { text: 'Net Unit Cost/FRT', align: 'start', sortable: true, filterable: true, class: 'green white--text', value: 'net_unit_cost', default_show: true },
          { text: 'Original Promotion Price', align: 'start', sortable: false, filterable: true, class: 'accent white--text', value: 'original_promotion_price', default_show: true },
          { text: 'Rules Promotion Price', align: 'start', sortable: false, filterable: true, class: 'accent white--text', value: 'rules_promotion_price', default_show: true },
          { sortable: true, class: 'accent white--text', value: 'data-table-expand', text: 'Store Detail', default_show: true, show: this.storeGroupIsSelected },
          { text: 'Multiplier @ SRP', align: 'center', sortable: true, filterable: true, class: 'accent white--text', value: 'promotion_price', width: '300', disabled: false, default_show: true },
          { text: 'Net GP%', align: 'start', sortable: true, filterable: false, class: 'accent white--text', value: 'net_gross_profit', default_show: true },
          { text: 'WALMART', align: 'center', sortable: true, filterable: false, class: 'green white--text', value: 'comp_pricing_WALMART', default_show: true },
          { text: 'Order Book Index', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'obxi', default_show: true },
          { text: 'Start Date', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'start_dt', default_show: true },
          { text: 'End Date', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'end_dt', default_show: true },
          { text: 'Updated On', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'updated_on', default_show: true, show: this.storeGroupIsSelected },
          { text: 'Rules Applied', align: 'start', sortable: false, filterable: true, class: 'accent white--text', value: 'rules_applied', default_show: true },
          { text: 'Promo Info', align: 'start', sortable: false, filterable: false, class: 'accent white--text', value: 'promo_info', default_show: true },
          { text: 'Group Manager Modified', align: 'start', sortable: false, filterable: true, class: 'accent white--text', value: 'group_manager_modified', default_show: false },
          { text: 'Category', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'category', default_show: false },
          { text: 'Vendor ID', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'vendor_id', default_show: false },
          { text: 'Vendor Name', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'vendor_name', default_show: false },
          { text: 'Case Cost', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'cost', default_show: false },
          { text: 'EBA', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'early_buy_allowance', default_show: false },
          { text: 'EDLC Billback', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'edlc_billback_allowance', default_show: false },
          { text: 'EDLC Scan', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'edlc_scan_allowance', default_show: false },
          { text: 'EDLC Scan ePay', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'edlc_scan_epay_allowance', default_show: false },
          { text: 'EDLC Scan Compete', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'edlc_scan_compete_allowance', default_show: false },
          { text: 'Linked Group', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'linked_group', default_show: false },
          { text: 'Like Code', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'like_code', default_show: false },
          { text: 'Components', align: 'start', sortable: false, filterable: false, class: 'accent white--text', value: 'is_shipper', default_show: false }
        ].filter(header => header.show === undefined || header.show)
      } else if (this.$auth.tenant == 'awg') {
        headers = [
          { sortable: false, class: 'accent', value: 'data-table-select', text: 'Checkbox' },
          { text: 'Included', align: 'start', sortable: false, filterable: true, class: 'accent white--text', value: 'excluded'},
          { text: 'Department', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'department' },
          { text: 'Department #', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'department_number' },
          { text: 'Rules Applied', align: 'start', sortable: false, filterable: true, class: 'accent white--text', value: 'rules_applied' },
          { text: 'Category #', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'category_number' },
          { text: 'Category Description', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'category' },
          { text: 'Sub-Category #', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'subcategory_number' },
          { text: 'Sub-Category Description', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'subcategory' },
          { text: 'Linked Group', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'linked_group' },
          { text: 'UPC', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'upc' },
          { text: 'Item #', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'item_id' },
          { text: 'Item Description', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'description' },
          { text: 'Pack', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'qty_per_case' },
          { text: 'Size', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'pack_size' },
          { text: 'Case Cost', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'cost' },
          { text: 'Reg Unit Cost', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'unit_cost' },
          { text: 'Promo Info', align: 'start', sortable: false, filterable: false, class: 'accent white--text', value: 'promo_info' }, // magnifying glass
          { text: 'AMAP', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'amap_allowance' },
          { text: 'TPR Billback', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'tpr_billback_allowance' },
          { text: 'TPR Scan', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'tpr_scan_allowance' },
          { text: 'EDLC Billback', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'edlc_billback_allowance' },
          { text: 'EDLC Scan', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'edlc_scan_allowance' },
          { text: 'Total Allowance Per Unit', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'total_allowance_per_unit' },
          { text: 'Net Unit Cost', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'net_unit_cost' },
          { text: 'Multiplier @ SRP', align: 'center', sortable: true, filterable: true, class: 'accent white--text', value: 'promotion_price', width: '240', disabled: false },
          { text: 'TPR GP%', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'net_gross_profit' },
          { text: 'Compliance Enforced', align: 'center', sortable: true, filterable: true, class: 'accent white--text', value: 'enforce_compliance' },
          { text: 'SRP Needed to Maintain Margin', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'srp_needed_to_maintain_margin' },
          { text: 'Difference to Regular Price', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'difference_to_regular_price' },
          { text: 'Current Retail Price', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'retail_price' },
          { text: 'Unit Savings', align: 'start', sortable: true, filterable: false, class: 'accent white--text', value: 'unit_savings', default_show: false },
          { text: 'Start Date', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'start_dt' },
          { text: 'End Date', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'end_dt' },
          { text: 'Components', align: 'start', sortable: false, filterable: false, class: 'accent white--text', value: 'is_shipper' },
          { text: 'Length of TPR', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'length_of_tpr' },
          { text: 'Previous TPR - Cost', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'prev_net_unit_cost' },
          { text: 'Previous TPR - MSU', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'prev_msu' },
          { text: 'Previous TPR - Retail', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'prev_retail' },
          { text: 'Previous TPR - Margin', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'prev_margin' },
          { text: 'Current TPR - MSU', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'current_tpr_msu' },
          { text: 'Current TPR - Retail', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'current_tpr_retail' },
          { text: 'Current TPR - End Date', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'current_tpr_end_date' }
        ]
      } else {
        headers = [
          { sortable: false, class: 'accent', value: 'data-table-select', text: 'Checkbox' },
          { text: 'Department', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'department' },
          { text: 'Included', align: 'start', sortable: false, filterable: true, class: 'accent white--text', value: 'excluded' },
          { text: 'Group Manager Modified', align: 'start', sortable: false, filterable: true, class: 'accent white--text', value: 'group_manager_modified' },
          { text: 'Rules Applied', align: 'start', sortable: false, filterable: true, class: 'accent white--text', value: 'rules_applied' },
          { text: 'Rules Promotion Price', align: 'start', sortable: false, filterable: true, class: 'accent white--text', value: 'rules_promotion_price' },
          { text: 'Original Promotion Price', align: 'start', sortable: false, filterable: true, class: 'accent white--text', value: 'original_promotion_price' },
          { text: 'Category', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'category' },
          { text: 'Linked Group', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'linked_group' },
          { text: 'UPC', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'upc' },
          { text: 'Item ID', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'item_id' },
          { text: 'Description', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'description' },
          { text: 'Vendor ID', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'vendor_id' },
          { text: 'Pack', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'qty_per_case' },
          { text: 'Size', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'pack_size' },
          { text: 'Case Cost', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'cost' },
          { text: 'Case Freight', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'case_freight' },
          { text: 'Reg Unit Cost', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'unit_cost' },
          { text: 'AMAP', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'amap_allowance' },
          { text: 'EBA', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'early_buy_allowance' },
          { text: 'TPR Billback', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'tpr_billback_allowance' },
          { text: 'TPR Scan', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'tpr_scan_allowance' },
          { text: 'EDLC Billback', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'edlc_billback_allowance' },
          { text: 'EDLC Scan', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'edlc_scan_allowance' },
          { text: 'Net Unit Cost', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'net_unit_cost' },
          { text: 'Compliance Enforced', align: 'center', sortable: true, class: 'accent white--text', value: 'enforce_compliance' },
          { text: 'Multiplier @ SRP', align: 'center', sortable: true, filterable: true, class: 'accent white--text', value: 'promotion_price', width: '240', disabled: false },
          { text: 'GP%', align: 'start', sortable: true, filterable: false, class: 'accent white--text', value: 'net_gross_profit' },
          { text: 'Retail Price', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'retail_price' },
          { text: 'Unit Savings', align: 'start', sortable: true, filterable: false, class: 'accent white--text', value: 'unit_savings', default_show: false },
          { text: 'Order Book Index', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'obxi' },
          { text: 'Like Code', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'like_code' },
          { text: 'Start Date', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'start_dt' },
          { text: 'End Date', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'end_dt' },
          { text: 'Promo Info', align: 'start', sortable: false, filterable: false, class: 'accent white--text', value: 'promo_info' },
          { text: 'Components', align: 'start', sortable: false, filterable: false, class: 'accent white--text', value: 'is_shipper' }
        ]
      }
      // If there is no defined default_show field on the header, then add a default_show to true - used when loading headers for the first time or resetting
      for (let header of headers) {
        if (!('default_show' in header)) {
          header.default_show = true
        }
      }
      if (this.tprProTenants) {
        this.banners.forEach(banner => {
          const headerValue = `comp_pricing_${banner}`
          if (!headers.some(header => header.value === headerValue)) {
            headers.push({
              text: banner.replace(/_/g, ' '),
              align: 'center',
              sortable: true,
              filterable: false,
              class: 'green white--text',
              value: headerValue,
              default_show: false
            })
          }
        })
        this.banners.length > 0 && headers.push({
          text: 'Competitor Pricing Mode',
          align: 'center',
          sortable: false,
          filterable: false,
          class: 'green white--text',
          value: 'comp_pricing_mode',
          width: '75',
          default_show: false
        })
      }
      if (!this.tprProTenants) {
        headers = headers.filter(header => !['original_promotion_price',  'rules_promotion_price', 'rules_applied', 'obxi', 'like_code', 'is_shipper','comp_pricing', 'case_freight'].includes(header.value))
        if (this.$auth.tenant !== 'awg') {
          headers = headers.filter(header => !['excluded'].includes(header.value))
        }
      }
      // ARG doesn't want these headers
      if (this.$auth.tenant === 'alliance-retail-group') {
        headers = headers.filter(header => !['like_code'].includes(header.value))
      }
      let vendorIdTenants = ['alliance-retail-group', 'pricechopper']
      if (!vendorIdTenants.includes(this.$auth.tenant)) {
        headers = headers.filter(header => header.value !== 'vendor_id')
      }
      // store users hide rules promo price and promo info.
      if (this.isTprProRetailUser) {
        headers = headers.filter(header => !['rules_promotion_price', 'promo_info'].includes(header.value))
      }
      if (!this.isTprProRetailUser || !this.tprProTenants) {
        headers = headers.filter(header => !['group_manager_modified'].includes(header.value))
      }
      return headers
    },
    compPricingModes() {
      if (this.$auth.tenant === 'alliance-retail-group') {
        const competitors = this.allHeaders
          .filter(h => /^comp_pricing_/.test(h.value))
          .map(h => h.value.replace(/^comp_pricing_/, ''))
          .sort()
        return competitors.includes('mode') ? competitors : ['mode', ...competitors]
      }
      return ['mode']
    }
  },
  methods: {
    retailMatchesAcrossStores(item) {
      const altSig = item.alternate_store_items
        .map(a => `${a.retail_price}:${a.promotion_price}`)
        .join(',')
      const sig = `${item.retail_price}|${item.promotion_price}|${altSig}`
      return this.cachedByItem('retailMatch', item, sig, () => {
        const itemRetail = Number(item.retail_price)
        const itemPromo = Number(item.promotion_price)
        return item.alternate_store_items.every(altItem =>
          Number(altItem.retail_price) === itemRetail && Number(altItem.promotion_price) === itemPromo
        )
      })
    },
    handleExpansion(item, state) {
      const itemIndex = this.expanded.indexOf(item);
      state ? this.expanded.splice(itemIndex, 1) : this.expanded.push(item);
    },
    toggleExpandAll() {
      if (this.allExpanded) {
        this.expanded = []
      } else {
        this.expanded = this.items.filter(i => i.alternate_store_items && i.alternate_store_items.length > 0)
      }
    },
    initializeBatchView() {
      if(!this.isTprProRetailUser) {
        this.tableChanged = this.tableChange(this.getItems, this.isTprProRetailUser)
        const savedChanges = localStorage.getItem(`changes_${this.selectedBatch.id}`)
        if (savedChanges) {
          this.changes = JSON.parse(savedChanges)
        }
        this.cleanupOtherBatchChanges()

        const { ad_group, tpr_date, tpr_end_date, selected_batch } = this.$route.params
        const {
          tpr_manager_ad_group,
          tpr_manager_tpr_date,
          tpr_manager_tpr_end_date,
          tpr_manager_selected_batch,
          tpr_manager_item_search
        } = sessionStorage

        this.selectedAdGroup = ad_group || JSON.parse(tpr_manager_ad_group)
        this.tprDate = tpr_date || tpr_manager_tpr_date || sessionStorage.getItem('selected_batch_tpr_date')
        this.tprEndDate = tpr_end_date || tpr_manager_tpr_end_date
        this.selectedBatch =
          selected_batch || JSON.parse(tpr_manager_selected_batch)
        this.searchValues = tpr_manager_item_search
          ? JSON.parse(tpr_manager_item_search)
          : []

        sessionStorage.setItem('selected_batch_tpr_date', this.tprDate)
        this.getItems()
        this.tableChanged = this.tableChange(this.getItems)
      } else {
        this.tableChanged = this.tableChange(this.getItems, this.isTprProRetailUser)
        const savedChanges = localStorage.getItem(`changes_${this.selectedBatch.id}`)
        if (savedChanges) {
          this.changes = JSON.parse(savedChanges)
        }
        this.cleanupOtherBatchChanges()
        const { store, store_party, tpr_date, tpr_end_date, selected_batch, user_stores } = this.$route.params
        const {
          tpr_pro_store,
          tpr_pro_store_group,
          tpr_manager_tpr_date,
          tpr_manager_tpr_end_date,
          tpr_manager_selected_batch,
          tpr_manager_item_search
        } = sessionStorage
        this.selectedStore = store || (tpr_pro_store ? JSON.parse(tpr_pro_store) : {})
        if (user_stores) this.userStores = user_stores
        this.selectedStoreParty = store_party || (tpr_pro_store_group ? JSON.parse(tpr_pro_store_group) : {})
        if (this.selectedStore?.store_party_id) {
          sessionStorage.setItem('tpr_pro_store', JSON.stringify(this.selectedStore))
        }
        if (this.selectedStoreParty?.store_party_id) {
          sessionStorage.setItem('tpr_pro_store_group', JSON.stringify(this.selectedStoreParty))
        }
        this.tprDate = tpr_date || tpr_manager_tpr_date || sessionStorage.getItem('selected_batch_tpr_date')
        this.tprEndDate = tpr_end_date || tpr_manager_tpr_end_date
        this.selectedBatch =
          selected_batch || JSON.parse(tpr_manager_selected_batch)
        this.searchValues = tpr_manager_item_search
          ? JSON.parse(tpr_manager_item_search)
          : []

        sessionStorage.setItem('selected_batch_tpr_date', this.tprDate)
        this.getItems()
        this.tableChanged = this.tableChange(this.getItems, this.isTprProRetailUser)
      }
    },
    loadItems() {
      this.getItems()
      // Ensure header indicators are always in sync after loading
      this.$nextTick(() => {
        this.updateHeaderSortIndicators()
      })
    },
    buildSearchExportVM() {
      const sortString = this.sortBy
        .map(([column, order]) => `${column} ${order}`)
        .join(', ')
      const storeIds = this.storeGroupIsSelected
        ? this.selectedStoreParty.children.flatMap(p => p.store_party_id)
        : [this.selectedStore.store_party_id]
      return {
        ...this.searchParams,
        tpr_date: this.tprDate,
        sort_by: sortString,
        store_ids: storeIds
      }
    },
    async getItems() {
      // Sorting a computed column (store detail, comp_pricing, unit savings, ...) fetches the full result set once and
      // paginates it in the browser. Re-slice the cache on page nav / sort toggles when the underlying dataset is unchanged, instead of refetching.
      const clientSorting = !this.isTprProRetailUser && this.hasClientSort
      if (clientSorting && this.fullClientItems.length > 0 && this.lastClientFetchSig === this.clientFetchSignature()) {
        await this.applyClientSort()
        return
      }
      const storeGroupPartyIds = this.storeGroupIsSelected ? this.selectedStoreParty.children.flatMap(p => p.store_party_id) : []
      const requestId = ++this.requestSequence
      this.activeRequestId = requestId
      this.loading = true
      const sortString = this.sortBy
        .map(([column, order]) => `${column} ${order}`)
        .join(', ')
      const params = {
        ...this.searchParams,
        tpr_date: this.tprDate,
        sort_by: clientSorting ? '' : sortString
      }
      try {
        // determine batch ID and store ID based on user type
        const batchId = this.isTprProRetailUser ? this.selectedBatch.tpr_batch_id : this.selectedBatch.id
        if (this.isTprProRetailUser) params.store_ids = this.storeGroupIsSelected ? this.selectedStoreParty.children.flatMap(p => p.store_party_id) : [this.selectedStore.store_party_id]
        const { data } = await (this.isTprProRetailUser ? retryApiCall(() =>
          Items.getItems(batchId, params, this.pageSize, this.from)
        ) : Items.getItems(batchId, params, this.pageSize, this.from))
        if (requestId !== this.activeRequestId) {
          return
        }
        this.formatAllSRPValues(data)
        if (!this.isTprProRetailUser && !clientSorting) {
          const requestedPage = this.page
          const currentPageKeys = data.map(item => this.getItemKey(item))
          const pagedForward = requestedPage > 1 && requestedPage > this.lastFetchedPage
          const isPastFinalPage = pagedForward &&
            (data.length === 0 || this.isDuplicatePageResponse(currentPageKeys, this.lastFetchedPageKeys))
          if (isPastFinalPage) {
            this.lastFetchWasFinalPage = true
            this.pageResetting = true
            this.page = requestedPage - 1
            this.from = (this.page - 1) * this.pageSize
            return
          }
          this.lastFetchedPageKeys = currentPageKeys
          this.lastFetchedPage = requestedPage
        }
        // Collect all distinct banners
        let allBanners = this.extractBanners(data)
        // Add distinct banners to banners array
        allBanners.forEach(banner => {
          if (!this.banners.includes(banner)) {
            this.banners.push(banner)
          }
        })
        let banners = this.banners
        const rowsByItemId = new Map()
        if (this.useStoreParties) {
          for (const row of data) {
            if (!rowsByItemId.has(row.item_id)) rowsByItemId.set(row.item_id, [])
            rowsByItemId.get(row.item_id).push(row)
          }
        }
        const updatedItems = data.map(item => {
          const changedItem = this.changes.find(change => change.id === item.id)
          let updatedItem = {
            ...item,
            ...changedItem,
            srp: changedItem?.srp || this.computedPromotionPrice({ ...item, ...changedItem })
          }
          updatedItem.srp = this.computedPromotionPrice(updatedItem)
          // Add comp_pricing_(banner) fields
          const bannerPrices = this.getBannerPrices(item)
          banners.forEach(banner => {
            const price = bannerPrices[banner.replace(/_/g, ' ')]
            updatedItem[`comp_pricing_${banner}`] = price ? parseFloat(price).toFixed(2) : 'N/A'
          })
          // add mode of comp_pricing columns to item
          const validPrices = banners.map(banner => parseFloat(this.getBannerPrice(item.comp_pricing, banner.replace(/_/g, ' ')))).filter(price => !isNaN(price) && price !== 0)
          if (validPrices.length === 1) {
            updatedItem.comp_pricing_mode = validPrices[0].toFixed(2)
          } else if (validPrices.length >= 2) {
            const priceCounts = validPrices.reduce((acc, val) => {
              acc[val] = (acc[val] || 0) + 1
              return acc
            }, {})
            const modePrice = Object.keys(priceCounts).reduce((a, b) => priceCounts[a] > priceCounts[b] ? a : b)
            if (priceCounts[modePrice] === 1) {
              updatedItem.comp_pricing_mode = Math.min(...validPrices).toFixed(2)
            } else {
              updatedItem.comp_pricing_mode = parseFloat(modePrice).toFixed(2)
            }
          } else if (validPrices.length > 0) {
            const lowestPrice = Math.min(...validPrices)
            updatedItem.comp_pricing_mode = lowestPrice.toFixed(2)
          } else {
            updatedItem.comp_pricing_mode = 'N/A'
          }
          if (this.useStoreParties) {
            attachStoreRows(item, updatedItem, {
              rowsByItemId,
              primaryStoreId: this.primaryStore.store_party_id,
              storeGroupPartyIds,
              changes: this.changes
            })
          }
          // engine SRP in the visible cell for retail v1 competitive items; user edit (version_id>1) wins.
          const isCompetitive = updatedItem.competitive_rule_applied === true || hasCompetitiveRuleApplied(updatedItem.rules_applied)
          if (isCompetitive && updatedItem.version_id === 1 && Number(updatedItem.rules_promotion_price) > 0 && !changedItem) {
            updatedItem.promotion_price = parseFloat(updatedItem.rules_promotion_price).toFixed(2)
          }
          return updatedItem
        })
        const autoExcluded = this.collectAutoExclusions(updatedItems)
        this.stampCascadedExclusionReasons(updatedItems)
        if (this.isTprProRetailUser) {
          this.fullMultiStoreItems = this.useStoreParties
            ? updatedItems.filter(item => item.is_primary_store_item || item.is_alt_store_item)
            : updatedItems
          if (this.hasClientSort) {
            await this.applyClientSort()
          } else {
            // Sort the in-memory set so server ties keep a stable order.
            if (this.sortBy.length) this.fullMultiStoreItems = this.sortFullSet(this.fullMultiStoreItems)
            const lastPage = Math.max(1, Math.ceil(this.fullMultiStoreItems.length / this.pageSize))
            if (this.page > lastPage) {
              this.pageResetting = true
              this.page = lastPage
            }
            this.sliceCurrentPage()
          }
        } else if (clientSorting) {
          // cache the full set, sort & slice locally.
          this.fullClientItems = updatedItems
          this.lastClientFetchSig = this.clientFetchSignature()
          await this.applyClientSort()
        } else {
          // server-paged, so the response is already the current page
          this.items = updatedItems
          this.lastFetchWasFinalPage = data.length < this.pageSize
          this.expanded = this.items
          await this.recomputeAdGroupNetGp()
        }
        await this.persistAutoExclusions(autoExcluded)
      } catch (err) {
        if (requestId === this.activeRequestId) {
          console.error(err)
          const message = err?.response?.data?.message || err.message
          this.alert(`No Matching Items Found: ${message}`)
        } else {
          console.debug('Discarded stale batch items response', err)
        }
      } finally {
        if (requestId === this.activeRequestId) {
          this.loading = false
        }
      }
    },
    async preloadEnforcedPromos() {
      // Populate enforced-compliance promos for the tooltip, and refresh net GP on load for
      // non-compliant or edited (version_id > 1) rows, whose persisted net GP can be stale.
      if (this.$auth.tenant === 'alliance-retail-group' && this.isTprProRetailUser) {
        const rows = []
        for (const item of this.items) {
          rows.push(item)
          if (item.alternate_store_items?.length) rows.push(...item.alternate_store_items)
        }
        rows.forEach(async item => {
          if (!this.srpIsCompliant(item) || item.version_id > 1) {
            await this.calculateGrossProfit(item)
          }
        })
      }
    },
    onFiltersCleared() {
      // This method can be called by the SearchFilters component when all filters are cleared
      // Force a state restoration by calling it directly
      if (this.savedStateBeforeFilters && !this.hasFiltersApplied()) {
        this.restoreStateAfterFiltersRemoved()
      }
    },
    updateDrawerHeight() {
      // Match the filter drawer to the full grid container height (toolbar + table + footer)
      // so it spans the grid and scrolls internally, with no white gap below the footer.
      this.$nextTick(() => {
        const el = this.$refs.batchGridRef
        if (el) this.drawerHeight = el.offsetHeight
      })
    },
    validateInputs(item) {
      if (item.promotion_price <= 0 || item.multiplier <= 0) {
        this.alert("SRP and Multiplier must be greater than zero.")
        return false
      }
      return true
    },
    handleEnterKey(currentRowIndex, fieldName, event) {
      event.preventDefault()
      let nextRow = currentRowIndex + 1
      // Target the specific field in next row
      let nextField = document.querySelector(
        `[data-row="${nextRow}"][data-field="${fieldName}"]`
      )
      // Skip disabled rows
      while (nextField && this.srpIsDisabled(nextField)) {
        nextRow += 1
        nextField = document.querySelector(
          `[data-row="${nextRow}"][data-field="${fieldName}"]`
        )
      }
      nextField?.focus()
      nextField?.select()
    },
    handleShuttleWholeBatch() {
      this.shuttleChanges(true)
    },
    blurActiveElement() {
      document.activeElement.blur()
    },
    saveCurrentSession() {
      if (this.$auth.tenant !== 'alliance-retail-group') return
      // For store users, use tpr_batch_id instead of id
      const batchId = this.isTprProRetailUser ? this.selectedBatch.tpr_batch_id : this.selectedBatch.id
      const sessionData = {
        batchId: batchId,
        timestamp: new Date().toISOString(),
        page: this.page,
        pageSize: this.pageSize,
        searchParams: this.searchParams,
        sortBy: this.sortBy,
        displayHeaders: this.displayHeaders,
        hideHeaders: this.hideHeaders,
        searchValues: this.searchValues,
        userEmail: this.userEmail,
        version: '1.0'
      }
      const now = new Date()
      const month = (now.getMonth() + 1).toString().padStart(2, '0')
      const day = now.getDate().toString().padStart(2, '0')
      const year = now.getFullYear()
      const hour = now.getHours().toString().padStart(2, '0')
      const minute = now.getMinutes().toString().padStart(2, '0')
      // Sanitize batch name for filename use
      const sanitizedBatchName = (this.selectedBatch.batch_name || `batch_${this.selectedBatch.id}`)
        .replace(/[^a-zA-Z0-9\-_]/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '')
      const filename = `${sanitizedBatchName}_${month}_${day}_${year}_${hour}_${minute}.json`
      const blob = new Blob([JSON.stringify(sessionData, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    },
    saveStateBeforeFilters() {
      // Save current page and sorting state before applying filters
      if (!this.savedStateBeforeFilters) {
        // Create a plain object to avoid Vue reactivity issues
        const plainSortBy = this.sortBy ? this.sortBy.map(item => [...item]) : []
        this.savedStateBeforeFilters = Object.freeze({
          page: this.page,
          sortBy: plainSortBy,
          displayHeaders: this.displayHeaders ? [...this.displayHeaders] : []
        })
      }
    },
    async restoreStateAfterFiltersRemoved() {
      // Restore page and sorting state when all filters are removed
      if (this.savedStateBeforeFilters && !this.hasFiltersApplied()) {
        const savedState = this.savedStateBeforeFilters
        const savedPage = savedState.page
        const savedSortBy = savedState.sortBy
        try {
          this.isRestoringState = true
          await this.$nextTick()
          this.sortBy.splice(0)
          if (savedSortBy && savedSortBy.length > 0) {
            savedSortBy.forEach(sortItem => {
              this.sortBy.push([...sortItem])
            })
          }
          this.updateHeaderSortIndicators()
          this.page = savedPage
          this.from = (savedPage - 1) * this.pageSize
          await this.$nextTick()
          this.loadItems()
        } finally {
          this.isRestoringState = false
          this.savedStateBeforeFilters = null
        }
      }
    },
    hasFiltersApplied() {
      // Check if any filters are currently applied
      return Object.keys(this.searchParams).some(key => {
        const value = this.searchParams[key]
        return Array.isArray(value) ? value.length > 0 : value !== null && value !== undefined && value !== ''
      })
    },
    getItemKey(item) {
      return item.id || `${item.item_id || ''}_${item.store_id || ''}`
    },
    isDuplicatePageResponse(currentKeys, previousKeys) {
      if (!previousKeys || currentKeys.length !== previousKeys.length) {
        return false
      }
      return currentKeys.every((key, idx) => key === previousKeys[idx])
    },
    onPageChanged(newPage) {
      // Update saved state when page changes manually (not via filter restoration)
      if (this.savedStateBeforeFilters && !this.hasFiltersApplied()) {
        // Create a new frozen object since the original is frozen
        this.savedStateBeforeFilters = Object.freeze({
          page: newPage,
          sortBy: this.savedStateBeforeFilters.sortBy,
          displayHeaders: this.savedStateBeforeFilters.displayHeaders
        })
      }
      if (this.isTprProRetailUser) {
        this.sliceCurrentPage()
      }
    },
    sliceCurrentPage() {
      const offset = (this.page - 1) * this.pageSize
      this.items = this.fullMultiStoreItems.slice(offset, offset + this.pageSize)
      this.syncRowRefsToItems()
      this.preloadEnforcedPromos()
    },
    // `expanded` and `selected` hold row objects, but the grid matches both by item-key, so
    // a reload leaves them pointing at rows that are no longer rendered. The table still
    // draws the right rows open and ticked; what breaks is our own code. handleExpansion
    // looks the clicked row up by identity, finds -1, and splices off the end of the array
    // instead -- collapsing some other row. The bulk-apply payloads read prices straight off
    // whatever objects `selected` still holds. Remap onto the instances now on the page.
    syncRowRefsToItems() {
      if (!this.expanded.length && !this.selected.length) return
      const onPage = new Map(this.items.map(row => [row.id, row]))
      // Remap, never filter: this also runs on every page change, and an id dropped here is
      // dropped from the grid's own state too, so a row expanded or ticked on page 1 would
      // come back collapsed or unticked. Rows off the current page keep their instance.
      // Returning the original array when nothing moved keeps the grid's watchers asleep.
      const remap = rows => {
        let changed = false
        const next = rows.map(row => {
          const fresh = onPage.get(row.id)
          if (!fresh || fresh === row) return row
          changed = true
          return fresh
        })
        return changed ? next : rows
      }
      this.expanded = remap(this.expanded)
      this.selected = remap(this.selected)
    },
    triggerFileInput() {
      if (this.$auth.tenant !== 'alliance-retail-group') return
      this.$refs.sessionFileInput.click()
    },
    handleFileSelect(event) {
      if (this.$auth.tenant !== 'alliance-retail-group') return
      const file = event.target.files[0]
      if (!file) return
      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const sessionData = JSON.parse(e.target.result)
          // For store users, compare against tpr_batch_id instead of id
          const currentBatchId = this.isTprProRetailUser ? this.selectedBatch.tpr_batch_id : this.selectedBatch.id
          if (sessionData.batchId !== currentBatchId) {
            this.alert('This session file is for a different batch')
            return
          }
          this.restoreSessionData(sessionData)
        } catch (error) {
          this.alert('Invalid session file format')
        }
      }
      reader.readAsText(file)
      // Clear the input value so the same file can be selected again
      event.target.value = ''
    },
    restoreSessionData(sessionData) {
      try {
        this.isSessionRestoring = true
        this.isRestoringState = true
        // Restore page and pageSize first to trigger watchers
        this.pageSize = sessionData.pageSize || 100
        this.page = sessionData.page || 1
        // Restore other session data
        this.searchParams = sessionData.searchParams || {}
        this.sortBy = sessionData.sortBy || []
        this.displayHeaders = sessionData.displayHeaders || []
        this.hideHeaders = sessionData.hideHeaders || []
        this.searchValues = sessionData.searchValues || []
        // Apply search filters if they exist
        if (this.$refs.searchFilters && Object.keys(this.searchParams).length > 0) {
          this.$refs.searchFilters.params = { ...this.searchParams }
          // Set selected filters based on searchParams
          this.$refs.searchFilters.selectedFilters = Object.keys(this.searchParams).filter(key => {
            const value = this.searchParams[key]
            return Array.isArray(value) ? value.length > 0 : value !== '' && value !== null && value !== undefined
          })
        }
        // Reload items with restored state and ensure pagination is synchronized
        this.$nextTick(() => {
          this.loadItems()
          this.$nextTick(() => {
            this.updateHeaderSortIndicators()
            this.isRestoringState = false
            this.isSessionRestoring = false
          })
        })
        this.notify('Session restored successfully')
      } catch (error) {
        this.alert('Error restoring session: ' + error.message)
        this.isRestoringState = false
        this.isSessionRestoring = false
      }
    },
    updateHeaderSortIndicators() {
      // Reset all headers first
      this.allHeaders.forEach(header => {
        header.showArrow = false
        header.descending = false
      })
      // Reset displayHeaders as well
      this.displayHeaders.forEach(header => {
        header.showArrow = false
        header.descending = false
      })
      // Update headers based on current sortBy state
      this.sortBy.forEach(([column, direction]) => {
        // Update allHeaders
        const allHeader = this.allHeaders.find(h => h.value === column)
        if (allHeader) {
          allHeader.showArrow = true
          allHeader.descending = direction === 'desc'
        }
        // Update displayHeaders
        const displayHeader = this.displayHeaders.find(h => h.value === column)
        if (displayHeader) {
          displayHeader.showArrow = true
          displayHeader.descending = direction === 'desc'
        }
      })
    },
    filterByLinkedGroup(linkedGroup) {
      // Alliance-only feature
      if (this.$auth.tenant !== 'alliance-retail-group') return
      // Save state before applying filter
      this.saveStateBeforeFilters()
      this.isRestoringState = true
      this.page = 1
      this.from = 0
      this.searchParams = {
        ...this.searchParams,
        linked_group: [linkedGroup]
      }
      this.$nextTick(() => {
        this.isRestoringState = false
        if (this.$refs.searchFilters) {
          this.$refs.searchFilters.params.linked_group = [linkedGroup]
          if (!this.$refs.searchFilters.selectedFilters.includes('linked_group')) {
            this.$refs.searchFilters.selectedFilters.push('linked_group')
          }
          this.$refs.searchFilters.triggerSearch()
        }
      })
      this.loadItems()
      this.$nextTick(() => {
        this.selectAllLinkedGroupItems(linkedGroup)
      })
    },
    selectAllLinkedGroupItems(linkedGroup) {
      const source = this.isTprProRetailUser ? this.fullMultiStoreItems : this.items
      const linkedGroupItems = source.filter(item =>
        item.linked_group === linkedGroup &&
        (item.excluded === '' || item.excluded === null)
      )
      if (!this.isEditingDisabled) this.selected = linkedGroupItems
    },
    handleSRPBlur(item, event) {
      const originalValue = event.target.value
      const truncatedValue = this.truncateToTwoDecimals(originalValue)
      if (originalValue !== String(truncatedValue)) {
        if (item) {
          item.promotion_price = truncatedValue
          this.updateGrossProfit(item)
          this.$nextTick(() => {
            if (event.target.getAttribute('data-field') === 'promotion_price') {
              this.srprefresh += 1
            }
          })
        } else {
          this.srp = truncatedValue
        }
      } else if (item) {
        this.debouncedUpdateGrossProfit.flush()
      }
      this.warnIfOverPassThrough(item)
      if (item && this.srpExceedsBaseRetail(item)) {
        this.alert('Item will not be included: its TPR SRP exceeds base retail.')
      }
      this.warnOnPropagatedBreaches(item)
      this.warnOnPropagatedBaseRetail(item)
    },
    // The multiplier is half of the per-unit price, so editing it can break the same two
    // rules an SRP edit can -- on this row and on every store it propagates to. It had no
    // blur at all, so those edits disabled Save with nothing said about why.
    onMultiplierBlur({ item }) {
      this.warnIfOverPassThrough(item)
      this.warnOnPropagatedBreaches(item)
      this.warnOnPropagatedBaseRetail(item)
    },
    warnIfOverPassThrough(item) {
      if (!item || !this.isTprProRetailUser || this.passPercentIsCompliant(item)) return
      const maxSRP = this.passPercentMaxSRP(item)
      if (maxSRP > 0) {
        this.alert(`Item ${item.item_id} must have an SRP at or below $${(maxSRP * item.multiplier).toFixed(2)}`)
      }
    },
    // Agreed handling for a propagated row going out of bounds: warn, open the rows that
    // broke so the user can see them without hunting, and leave Save disabled until they
    // change something. Deliberately not a force-through, and deliberately not a partial
    // save that reports success for the rows that happened to fit.
    warnOnPropagatedBreaches(item) {
      if (!item) return
      const offenders = this.propagatedBreaches.get(item.id)
      if (!offenders || !offenders.length) return
      this.expandRow(item)
      const names = [...new Set(offenders.map(o => this.storeLabelFor(o.row)))]
      const shown = names.slice(0, 3).join(', ')
      const more = names.length > 3 ? `, and ${names.length - 3} more` : ''
      const rule = this.maxSrpRulesFor(offenders.map(o => o.row))
      this.alert(
        `${item.item_id}: SRP is above the ${rule} for ${offenders.length} ` +
        `other store row(s) - ${shown}${more}. Expanded below; change the SRP to save.`
      )
    },
    // Only reached when nothing blocks the save: a pass-through breach is the more urgent
    // message and would otherwise be replaced by this one in the snackbar.
    warnOnPropagatedBaseRetail(item) {
      if (!item || this.propagatedBreaches.has(item.id)) return
      const offenders = this.propagatedBaseRetailWarnings.get(item.id)
      if (!offenders || !offenders.length) return
      this.expandRow(item)
      const names = [...new Set(offenders.map(o => this.storeLabelFor(o)))]
      const shown = names.slice(0, 3).join(', ')
      const more = names.length > 3 ? `, and ${names.length - 3} more` : ''
      const stranded = offenders.filter(o => this.rowIsExcluded(o)).length
      const tail = stranded
        ? ` ${stranded} of those are excluded and cannot be included again until it is lowered.`
        : ''
      this.alert(
        `${item.item_id}: this SRP exceeds base retail for ${offenders.length} other store ` +
        `row(s) - ${shown}${more}.${tail}`
      )
    },
    storeLabelFor(row) {
      // the group members, same source the cascaded table is handed for its store column
      const store = this.selectedStoreParty?.children?.find(s => s.store_party_id === row.store_id)
      return store?.store_name || row.store_name || row.store_id
    },
    // `expanded` holds row objects, so expand the instance the grid is actually rendering.
    expandRow(item) {
      const row = this.items.find(i => i.id === item.id) || item
      if (!this.expanded.includes(row)) this.expanded.push(row)
    },
    formatAllSRPValues(items) {
      items.forEach(item => {
        if (item.promotion_price !== null && item.promotion_price !== undefined) {
          item.promotion_price = parseFloat(item.promotion_price).toFixed(2)
        }
      })
    },
    extractBanners(data) {
      let allBanners = new Set()
      data.forEach(item => {
        if (item.comp_pricing) {
          try {
            const parsedPricing = JSON.parse(item.comp_pricing)
            parsedPricing.forEach(banner => {
              if (banner.banner) {
                //replace spaces with underscores
                banner.banner = banner.banner.replace(/\s/g, '_')
                allBanners.add(banner.banner)
              }
            })
          } catch (e) {
            console.error('Failed to parse comp_pricing:', e)
          }
        }
      })
      // Always add walmart as a banner to ensure we get N/A for walmart values since WALMART header in included by default
      if (!allBanners.has("WALMART")) { allBanners.add("WALMART") }
      return allBanners
    },
    getBannerPrice(compPricing, banner) {
      if (!compPricing) return 0
      try {
        const parsedPricing = JSON.parse(compPricing)
        const bannerData = parsedPricing.find(b => b.banner === banner)
        return bannerData ? parseFloat(bannerData.regular_price_mode) : 0
      } catch (e) {
        console.error('Failed to parse comp_pricing:', e)
        return 0
      }
    },
    async updateGrossProfit(item) {
      item.net_gross_profit = await this.calculateGrossProfit(item)
      // Carry the recomputed GP on the pending change so the saved GP matches the saved price
      // even when the row is no longer held in memory by the time Save runs.
      const pendingChange = this.changes.find(c => c.id === item.id)
      if (pendingChange) pendingChange.net_gross_profit = item.net_gross_profit
      if (
        this.propagateChangesToChildRows &&
        this.storeGroupIsSelected &&
        item.alternate_store_items?.length > 0
      ) {
        const updated = []
        for (const asi of item.alternate_store_items) {
          // Snapshot persisted value so resetRow can revert it.
          if (!this.originalItems.find(orig => orig.id === asi.id)) {
            this.originalItems.push(Object.freeze({ ...asi }))
          }
          const next = {
            ...asi,
            promotion_price: item.promotion_price,
            multiplier: item.multiplier,
          }
          next.net_gross_profit = await this.calculateGrossProfit(next)
          updated.push(next)
        }
        item.alternate_store_items = updated
      }
    },
    async calculateGrossProfit(item) {
      const storeId = item.store_id || this.selectedStore?.store_party_id
      await this.ensureEnforcedPromos(item, this.selectedBatch?.tpr_batch_id, storeId)
      const isPerishable = String(this.complianceBatch?.batch_type || '').toUpperCase() === 'PERISHABLE'
      return this.calculateNetGrossProfit(item, item.promotion_price, item.multiplier, isPerishable)
    },
    // readUserHeaderLayouts() lives in the shared mixin - it is also what batch export reads.
    persistUserHeaders() {
      const allUserHeaders = JSON.parse(localStorage.getItem("userHeaders")) || {}
      const layouts = this.readUserHeaderLayouts()
      layouts[this.headerLayoutMode] = {
        order: this.selectedHeaders,
        known: this.allHeaders.map(h => h.value)
      }
      layouts.lastMode = this.headerLayoutMode
      allUserHeaders[this.userEmail] = layouts
      localStorage.setItem("userHeaders", JSON.stringify(allUserHeaders))
    },
    syncHeadersOrder(writeToStorage = true) {
      if (writeToStorage) {
        this.persistUserHeaders()
      }
      const reorderedHeaders = this.allHeaders.filter(header =>
        this.selectedHeaders.includes(header.value)
      )
      this.displayHeaders = reorderedHeaders.sort((a, b) => {
        return this.selectedHeaders.indexOf(a.value) - this.selectedHeaders.indexOf(b.value)
      })
      this.hideHeaders = this.allHeaders.filter(header =>
        !this.selectedHeaders.includes(header.value)
      )
    },
    loadUserHeaders() {
      const stored = this.readUserHeaderLayouts()[this.headerLayoutMode]
      if (!stored || !Array.isArray(stored.order)) {
        this.resetHeaders()
        return
      }
      // Drop values that are not real columns any more. This is load-bearing: renamed columns
      // left in a user's localStorage used to reach the export API and 500 it, so selectedHeaders
      // must never carry a value that isn't in allHeaders. Nothing is lost by filtering here -
      // this never writes to storage, and the allHeaders watcher re-runs the load once the
      // late-arriving comp_pricing_* banner columns exist.
      const validValues = new Set(this.allHeaders.map(h => h.value))
      const saved = stored.order.filter(h => validValues.has(h))
      const known = new Set(stored.known || stored.order)
      const newDefaults = this.allHeaders
        .filter(h => h.default_show && !known.has(h.value) && !saved.includes(h.value))
        .map(h => h.value)
      this.selectedHeaders = [...saved, ...newDefaults]
      this.syncHeadersOrder(false)
    },
    saveHeadersOrder() {
      this.persistUserHeaders()
      this.showModal = false
    },
    resetHeaders() {
      // syncHeadersOrder() persists, and persistUserHeaders() only touches the current mode's
      // slot, so this resets the layout for the mode in view and leaves the other one alone.
      this.selectedHeaders = this.allHeaders.flatMap(header =>
          header.default_show ? header.value : []
      )
      this.syncHeadersOrder()
      this.showModal = false
    },
    onDragEnd() {
      // Ensure checkbox header remains in displayHeaders
      if (!this.displayHeaders.some(header => header.value === 'data-table-select')) {
        const checkboxHeader = this.allHeaders.find(header => header.value === 'data-table-select')
        if (checkboxHeader) {
          this.displayHeaders.unshift(checkboxHeader)
          this.hideHeaders = this.hideHeaders.filter(header => header.value !== 'data-table-select')
        }
      }
      const displayHeaderValues = this.displayHeaders.map(header => header.value)
      this.selectedHeaders = displayHeaderValues
    },
    async getPromosByRow(item) {
      const { id } = item
      const tprId = id
      try {
        const { data } = await Items.getPromosByRow(
          this.selectedBatch.id,
          tprId
        )
        this.selectedRow = data
      } catch (err) {
        console.log(err)
      }
    },
    openModal() {
      this.showModal = true
    },
    openLink(promoId) {
      let baseUrl = process.env.VUE_APP_PROMO_PORTAL
      let prefix = this.$auth.tenantURLPrefix
      if (prefix) {
        baseUrl = `${prefix}.${baseUrl}`
      }
      const url = `https://${baseUrl}/promo?id=${promoId}`
      window.open(url)
    },
    async loadPromotionData(item, promotionCategory) {
      if (this.$auth.tenant !== 'alliance-retail-group') {
        return
      }
      if (this.selectedPromotionItem === item && 
          this.selectedPromotionCategory === promotionCategory) {
        return
      }
      this.selectedPromotionItem = item
      this.selectedPromotionCategory = promotionCategory
      this.loadingPromoPrice = true
      this.promoPromotionPriceData = null
      if (promotionCategory === 'tpr_scan_epay') {
        this.selectedPromotionAllowance = item.tpr_scan_epay_allowance
      } else if (promotionCategory === 'tpr_scan_compete') {
        this.selectedPromotionAllowance = item.tpr_scan_compete_allowance
      }
      try {
        let promoResponse
        if (this.isTprProRetailUser) {
          promoResponse = await Rules.getPromoPromotionPrice(
            this.selectedBatch.tpr_batch_id,
            item.item_id,
            promotionCategory
          )
        } else {
          promoResponse = await Items.getPromoPromotionPrice(
            this.selectedBatch.id,
            item.item_id,
            promotionCategory
          )
        }
        this.promoPromotionPriceData = promoResponse.data
      } catch (promoErr) {
        console.warn('Could not fetch promotion data from api_promo.promotion table:', promoErr)
      } finally {
        this.loadingPromoPrice = false
      }
    },
    async toggleExcluded(item) {
      if (this.isEditingDisabled || this.isReadOnlyUser) return
      const currentlyExcluded = !(item.excluded === '' || item.excluded === null)
      if (currentlyExcluded && this.srpExceedsBaseRetail(item)) {
        this.alert('Item cannot be included: its TPR SRP exceeds base retail.')
        return
      }
      try {
        if (item.excluded === '' || item.excluded === null) item.excluded = 'manual'
        else if (item.excluded) item.excluded = ''
        else if (item.excluded === 'manual') item.excluded = ''
        if (!this.isTprProRetailUser) {
          await Items.updateExcludeInclude(item.id, item.excluded)
        } else {
          await Rules.saveExclusion(item.id, this.$auth.user.email, item.excluded)
          if (this.storeGroupIsSelected && this.propagateChangesToChildRows && item.alternate_store_items?.length > 0) {
            const itemToUpdate = this.itemsById.get(item.id)
            const primaryReason = this.getExclusionReason(item)
            const updatedAltItemItems = item.alternate_store_items.map(asi => {
              asi.excluded = item.excluded
              const exclusionReason = item.excluded ? primaryReason : ''
              asi.exclusion_reason = exclusionReason
              Rules.saveExclusion(asi.id, this.$auth.user.email, asi.excluded)
              return {
                ...asi,
                excluded: item.excluded,
                exclusion_reason: exclusionReason
              }
            })
            if (itemToUpdate) itemToUpdate.alternate_store_items = updatedAltItemItems
          }
        }
        this.markBatchDataChanged()
        this.$gtag.event('item_exclude_toggle', {
          excluded: Boolean(item.excluded),
          batch_type: this.selectedBatch?.batch_type
        })
      } catch (error) {
        console.error('Error while toggling exclusion: ', error)
      }
    },
    // Emitted after a store row's exclusion was saved.
    alternateItemsUpdate(itemId, alternateStoreItems) {
      const itemToUpdate = this.itemsById.get(itemId)
      if (itemToUpdate) itemToUpdate.alternate_store_items = alternateStoreItems
      this.markBatchDataChanged()
    },
    findItemForChange(id) {
      return this.itemsById.get(id) || null
    },
    // The row as the user currently sees it: persisted values with the pending edit applied.
    // Validation and payload building read this, never the raw row -- cascaded rows are edited
    // in a child component and never mutated here, so the raw row still holds the old price.
    effectiveRow(change) {
      const item = this.itemsById.get(change.id)
      if (!item) return null
      const merged = { ...item }
      for (const key of Object.keys(change)) {
        if (key === 'id' || key === 'item_id') continue
        merged[key] = change[key]
      }
      return merged
    },
    handleSubItemChange({ id, field, value, item_id, item }) {
      if (this.isEditingDisabled) return
      const altItem = this.findItemForChange(id)
      if (!altItem) return
      const existingChange = this.changes.find(change => change.id === id)
      if (existingChange) {
        existingChange[field] = value
      } else {
        if (!this.originalItems.find(orig => orig.id === id)) {
          this.originalItems.push(Object.freeze({ ...altItem }))
        }
        this.changes.push({ id, [field]: value, item_id })
      }
      // The cascaded table edits its own clone, so without this the row the guardrails
      // read keeps the old price. Mirrors handleInput, including the multiplier rule.
      altItem[field] = value
      if (item && item !== altItem) item[field] = value
      if (field === 'promotion_price' && Number(altItem.multiplier) > 1) {
        const change = this.changes.find(c => c.id === id)
        if (change && change.multiplier === undefined) {
          altItem.multiplier = 1
          if (item && item !== altItem) item.multiplier = 1
          change.multiplier = 1
        }
      }
      if (field === 'promotion_price' || field === 'multiplier') {
        this.debouncedUpdateGrossProfit(item || altItem)
      }
    },
    // The pending change lives here, not in the cascaded table, so a per-row reset there
    // has to clear it here or the Save button stays blocked with nothing to show for it.
    handleSubItemReset(id) {
      if (this.isEditingDisabled) return
      const original = this.originalItems.find(orig => orig.id === id)
      const altItem = this.findItemForChange(id)
      if (original && altItem) {
        this.$set(altItem, 'multiplier', original.multiplier)
        this.$set(altItem, 'promotion_price', original.promotion_price)
        this.$set(altItem, 'net_gross_profit', original.net_gross_profit)
      }
      this.changes = this.changes.filter(c => c.id !== id)
      this.originalItems = this.originalItems.filter(o => o.id !== id)
    },
    handleSubItemsSaved(savedIds) {
      if (!savedIds?.length) return
      const idSet = new Set(savedIds)
      this.changes = this.changes.filter(c => !idSet.has(c.id))
      this.originalItems = this.originalItems.filter(i => !idSet.has(i.id))
    },
    updateColumnWidths() {
      if (!this.$refs.dataTable || !this.$refs.dataTable.$el) {
        this.columnWidths = []
        return
      }
      const headerCells = this.$refs.dataTable.$el.querySelectorAll('th')
      this.columnWidths = Array.from(headerCells).map(cell => cell.offsetWidth)
    },
    getRowClass(item) {
      const isChanged = this.changedItemIds.has(item.id)
      const sig = `${item.gross_profit_percent}|${item.promotion_price}|${item.multiplier}|${item.excluded}|${item.top_sellers}|${item.srp}|${item.original_promotion_price}|${item.variance}|${item.rules_promotion_price}|${item.retail_price}|${item.max_srp_for_passthrough_percent}|${isChanged}`
      return this.cachedByItem('rowClass', item, sig, () => {
        let classList = ''
        const promotion_price = item.promotion_price !== undefined ? item.promotion_price : item.srp
        if (item.gross_profit_percent === null || promotion_price <= 0 || item.multiplier <= 0) {
          return 'red white--text '
        }
        if (this.tprProTenants) {
          // Don't dim rows excluded for exceeding retail; their SRP field stays editable.
          if (item.excluded !== '' && !this.srpExceedsBaseRetail(item)) classList += 'disabled-row '
          if (item.gross_profit_percent === null) classList += 'red white--text '
        } else {
          if (item.gross_profit_percent === null) classList += 'red white--text '
        }
        if (isChanged) {
          if (!this.srpIsCompliant(item) && this.blockNonCompliantSaves) return 'red white--text '
          if (this.tprProTenants && this.isTprProRetailUser && !this.passPercentIsCompliant(item)) {
            return 'red white--text'
          }
          if (this.tprProTenants) classList += 'yellow-row '
        }
        // Top Sellers are highlighted for ARG unless other styles are applied
        if (!classList && this.$auth.tenant === 'alliance-retail-group' && item.top_sellers) classList += 'light-blue lighten-4 '
        return classList.trim()
      })
    },
    computedPromotionPrice(item) {
      const price = selectDisplayPrice(item, this.isTprProRetailUser, this.tprProTenants)
      return this.formatCurrencyDisplay(price)
    },
    async handleSort(header) {
      // Save state before sorting if no filters are applied and no state is saved yet
      // (this captures the initial sorted state before any filters)
      if (header.sortable) {
        if (!this.hasFiltersApplied() && !this.savedStateBeforeFilters) {
          this.saveStateBeforeFilters()
        }
        const existingSortIndex = this.sortBy.findIndex(([column]) => column === header.value)
        if (!['comp_pricing_mode', 'promo_info'].includes(header.value)) {
          if (existingSortIndex === -1) {
            this.sortBy.push([header.value, 'asc'])
            this.contextColumn = header.value
            header.descending = false
            header.showArrow = true
          } else {
            const currentOrder = this.sortBy[existingSortIndex][1]
            if (currentOrder === 'asc') {
              this.sortBy[existingSortIndex][1] = 'desc'
              header.descending = true
              this.contextColumn = header.value
            } else {
              // Remove the sort entirely instead of going back to asc
              this.sortBy.splice(existingSortIndex, 1)
              header.descending = false
              header.showArrow = false
              this.contextColumn = header.value
            }
          }
          if (!this.hasFiltersApplied() && this.savedStateBeforeFilters) {
            this.savedStateBeforeFilters = Object.freeze({
              page: this.savedStateBeforeFilters.page,
              sortBy: this.sortBy.map(item => [...item]),
              displayHeaders: this.savedStateBeforeFilters.displayHeaders
            })
          }
          this.updateHeaderSortIndicators()
          if (this.hasClientSort) {
            await this.applyClientSortFlow()
          } else {
            this.loadItems()
          }
        }
      }
    },
    calculateUnitSavings(item) {
      const multiplier = Number(item.multiplier) || 1
      if (multiplier <= 0) return 0
      const perUnitSrp = (Number(item.promotion_price) || 0) / multiplier
      return (Number(item.retail_price) || 0) - perUnitSrp
    },
    evaluateAutoExclusion(row, flipped) {
      if (!row) return
      const included = row.excluded === '' || row.excluded === null
      if (included && this.srpExceedsBaseRetail(row)) {
        flipped.push({ row, prev: row.excluded })
        row.excluded = 'manual'
      }
    },
    collectAutoExclusions(items) {
      const flipped = []
      items.forEach(item => {
        this.evaluateAutoExclusion(item, flipped)
        if (Array.isArray(item.alternate_store_items)) {
          item.alternate_store_items.forEach(asi => this.evaluateAutoExclusion(asi, flipped))
        }
      })
      return flipped
    },
    stampCascadedExclusionReasons(items) {
      // Mirror each primary's reason onto its excluded store rows for the hover note.
      items.forEach(item => {
        if (!Array.isArray(item.alternate_store_items) || !item.alternate_store_items.length) return
        const primaryReason = this.getExclusionReason(item)
        item.alternate_store_items.forEach(asi => {
          const excluded = !(asi.excluded === '' || asi.excluded === null)
          asi.exclusion_reason = excluded ? primaryReason : ''
        })
      })
    },
    async persistAutoExclusions(flipped) {
      if (!flipped.length) return
      // Unique payload by id; revert all mutated rows on failure.
      const seen = new Set()
      const payload = []
      for (const { row } of flipped) {
        if (seen.has(row.id)) continue
        seen.add(row.id)
        payload.push({ id: row.id, excluded: row.excluded })
      }
      try {
        if (this.isTprProRetailUser) await Rules.saveBulkExclusions(payload)
        else await Items.updateExcludeIncludeBulk(payload)
        this.markBatchDataChanged()
      } catch (err) {
        flipped.forEach(({ row, prev }) => { row.excluded = prev })
        console.error('Failed to persist auto-exclusions (SRP exceeds base retail); reverted in-memory state:', err)
        this.alert('Some items could not be auto-excluded (TPR SRP exceeds base retail) and remain included. Please retry.')
      }
    },
    isClientSortColumn(column) {
      // derived columns the server can't order (sorted in browser)
      if (['unit_savings', 'difference_to_regular_price', 'data-table-expand'].includes(column)) return true
      return column.startsWith('comp_pricing_') && column !== 'comp_pricing_mode'
    },
    clientSortValue(item, column) {
      if (column === 'unit_savings') return this.calculateUnitSavings(item)
      if (column === 'difference_to_regular_price') return this.calculateDifferenceToRegularPrice(item)
      if (column === 'data-table-expand') return this.storeDetailRank(item)
      if (column.startsWith('comp_pricing_')) {
        const banner = column.replace('comp_pricing_', '').replace(/_/g, ' ')
        return this.getBannerPrice(item.comp_pricing, banner)
      }
      return item[column]
    },
    compareForSort(a, b) {
      const aEmpty = a === null || a === undefined || a === '' || a === 'N/A'
      const bEmpty = b === null || b === undefined || b === '' || b === 'N/A'
      if (aEmpty && bEmpty) return 0
      if (aEmpty) return -1
      if (bEmpty) return 1
      const na = Number(a)
      const nb = Number(b)
      if (!isNaN(na) && !isNaN(nb)) return na - nb
      return String(a).localeCompare(String(b))
    },
    compareRowIdentity(a, b) {
      // Stable tiebreak: Item ID then row id.
      return String(a?.item_id ?? '').localeCompare(String(b?.item_id ?? '')) ||
        String(a?.id ?? '').localeCompare(String(b?.id ?? ''))
    },
    sortFullSet(items) {
      const sorted = [...items]
      sorted.sort((a, b) => {
        for (const [column, order] of this.sortBy) {
          const cmp = this.compareForSort(this.clientSortValue(a, column), this.clientSortValue(b, column))
          if (cmp !== 0) return order === 'desc' ? -cmp : cmp
        }
        return this.compareRowIdentity(a, b)
      })
      return sorted
    },
    async applyClientSort() {
      // sort the full in-memory set by the active sort chain, then slice the page.
      const isRetail = this.isTprProRetailUser
      const sorted = this.sortFullSet(isRetail ? this.fullMultiStoreItems : this.fullClientItems)
      if (isRetail) this.fullMultiStoreItems = sorted
      else this.fullClientItems = sorted
      const lastPage = Math.max(1, Math.ceil(sorted.length / this.pageSize))
      if (this.page > lastPage) {
        this.pageResetting = true
        this.page = lastPage
      }
      const offset = (this.page - 1) * this.pageSize
      this.items = sorted.slice(offset, offset + this.pageSize)
      if (isRetail) {
        this.syncRowRefsToItems()
        this.preloadEnforcedPromos()
      } else {
        this.expanded = this.items
        await this.recomputeAdGroupNetGp()
      }
    },
    async applyClientSortFlow() {
      if (this.isTprProRetailUser) {
        await this.applyClientSort()
      } else {
        this.loadItems()
      }
    },
    clientFetchSignature() {
      return JSON.stringify({
        isTprProRetailUser: this.isTprProRetailUser,
        batchId: this.selectedBatch?.id,
        tprBatchId: this.selectedBatch?.tpr_batch_id,
        tprDate: this.tprDate,
        useStoreParties: this.useStoreParties,
        storeGroupIsSelected: this.storeGroupIsSelected,
        storeId: this.selectedStore?.store_party_id,
        storeGroupId: this.selectedStoreParty?.store_party_id,
        storeChildIds: this.storeGroupIsSelected
          ? (this.selectedStoreParty?.children || []).map(c => c.store_party_id)
          : null,
        searchParams: this.searchParams,
        searchValues: this.searchValues
      })
    },
    async recomputeAdGroupNetGp() {
      if (this.isTprProRetailUser || !this.tprProTenants) return
      await Promise.all(
        this.items
          .filter(item => item.version_id === 1 && item.rules_applied)
          .map(async item => {
            item.net_gross_profit = await this.calculateGrossProfit(item)
          })
      )
    },
    storeDetailRank(item) {
      if (!item.alternate_store_items || item.alternate_store_items.length === 0) return 2
      return this.retailMatchesAcrossStores(item) ? 1 : 0
    },
    applyStoreDetailSort() {
      const sortEntry = this.sortBy.find(([col]) => col === 'data-table-expand')
      if (!sortEntry) return
      const order = sortEntry[1]
      this.fullMultiStoreItems.sort((a, b) => {
        const cmp = this.storeDetailRank(a) - this.storeDetailRank(b)
        return order === 'asc' ? cmp : -cmp
      })
    },
    async removeSort(header) {
      const indexToRemove = this.sortBy.findIndex(([column]) => column === header.value)
      if (indexToRemove !== -1) {
        this.sortBy.splice(indexToRemove, 1)
      }
      // Update header indicators
      this.updateHeaderSortIndicators()
      this.loadItems()
    },
    async removeAllSorts() {
      // Clear all sorting
      this.sortBy = []
      // Update header indicators
      this.updateHeaderSortIndicators()
      // If we have saved state and no filters are applied, update the saved state
      if (this.savedStateBeforeFilters && !this.hasFiltersApplied()) {
        // Create a new frozen object since the original is frozen
        this.savedStateBeforeFilters = Object.freeze({
          page: this.savedStateBeforeFilters.page,
          sortBy: [],
          displayHeaders: this.savedStateBeforeFilters.displayHeaders
        })
      }
      // Re-fetch the items without sorting
      this.loadItems()
    },
    async loadUniqueValues() {
      let batchId = this.selectedBatch.id
      //If batch id is set
      if (batchId) {
        if (this.isTprProRetailUser) {
          try {
            const categoryResponse = await Rules.getUniqueValues(batchId, 'category')
            this.categoryList = Array.isArray(categoryResponse.data) ? categoryResponse.data : []
          } catch (error) {
            this.alert('Error loading search category values')
            console.error(`Failed to load category values: ${error.message}`)
          }
          this.subcategoryList = null
          try {
            const departmentResponse = await Rules.getUniqueValues(batchId, 'department')
            this.departmentList = Array.isArray(departmentResponse.data) ? departmentResponse.data : []
          } catch (error) {
            this.alert('Error loading search department values')
            console.error(`Failed to load department values: ${error.message}`)
          }
          try {
            const promoCategoryResponse = await Rules.getUniqueValues(batchId, 'promo_category_name')
            this.promoCategoryList = Array.isArray(promoCategoryResponse.data) ? promoCategoryResponse.data : []
          } catch (error) {
            this.alert('Error loading search promo category values')
            console.error(`Failed to load promo category values: ${error.message}`)
          }
          try {
            const vendorResponse = await Rules.getUniqueValues(batchId, 'vendor')
            this.vendorList = Array.isArray(vendorResponse.data) ? vendorResponse.data : []
          } catch (error) {
            this.alert('Error loading search vendor values')
            console.error(`Failed to load vendor values: ${error.message}`)
          }
      } else {
          try {
            const categoryResponse = await Items.getUniqueValues(batchId, 'category')
            this.categoryList = Array.isArray(categoryResponse.data) ? categoryResponse.data : []
          } catch (error) {
            this.alert('Error loading search category values')
            console.error(`Failed to load category values: ${error.message}`)
          }
          if (this.$auth.tenant == 'awg') {
            try {
              const subcategoryResponse = await Items.getUniqueValues(batchId, 'subcategory')
              this.subcategoryList = Array.isArray(subcategoryResponse.data) ? subcategoryResponse.data : []
            } catch (error) {
              this.alert('Error loading search subcategory values')
              console.error(`Failed to load subcategory values: ${error.message}`)
            }
          }
          else {
            this.subcategoryList = null
          }
          try {
            const departmentResponse = await Items.getUniqueValues(batchId, 'department')
            this.departmentList = Array.isArray(departmentResponse.data) ? departmentResponse.data : []
          } catch (error) {
            this.alert('Error loading search department values')
            console.error(`Failed to load department values: ${error.message}`)
          }
          try {
            const promoCategoryResponse = await Items.getUniqueValues(batchId, 'promo_category_name')
            this.promoCategoryList = Array.isArray(promoCategoryResponse.data) ? promoCategoryResponse.data : []
          } catch (error) {
            this.alert('Error loading search promo category values')
            console.error(`Failed to load promo category values: ${error.message}`)
          }
          try {
            const vendorResponse = await Items.getUniqueValues(batchId, 'vendor')
            this.vendorList = Array.isArray(vendorResponse.data) ? vendorResponse.data : []
          } catch (error) {
            this.alert('Error loading search vendor values')
            console.error(`Failed to load vendor values: ${error.message}`)
          }
        }
      }
    },
    async resetRow(item) {
      this.loading = true
      try {
        const originalItem = this.originalItems.find(origItem => origItem.id === item.id)
        if (originalItem) {
          this.$set(item, 'multiplier', originalItem.multiplier)
          this.$set(item, 'promotion_price', originalItem.promotion_price)
          this.$set(item, 'net_gross_profit', originalItem.net_gross_profit)
          // Revert cascade rows too.
          if (item.alternate_store_items?.length > 0) {
            const restoredAlts = item.alternate_store_items.map(asi => {
              const origAsi = this.originalItems.find(o => o.id === asi.id)
              return origAsi
                ? { ...asi, multiplier: origAsi.multiplier, promotion_price: origAsi.promotion_price, net_gross_profit: origAsi.net_gross_profit }
                : asi
            })
            this.$set(item, 'alternate_store_items', restoredAlts)
            const altIds = new Set(restoredAlts.map(a => a.id))
            this.changes = this.changes.filter(change => !altIds.has(change.id))
          }
          this.srprefresh++
          const itemIndex = this.items.findIndex(i => i.id === item.id)
          if (itemIndex !== -1) {
            this.$set(this.items, itemIndex, { ...this.items[itemIndex], multiplier: originalItem.multiplier, promotion_price: originalItem.promotion_price })
          }
        }
        this.changes = this.changes.filter(change => change.id !== item.id)
        this.notify('Row reset successfully')
      } catch (err) {
        console.error(err)
        const message = err?.response?.data?.message || err.message
        this.alert(`Error resetting values: ${message}`)
      } finally {
        this.loading = false
      }
    },
    cleanupOtherBatchChanges() {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key.startsWith('changes_') && key !== `changes_${this.selectedBatch.id}`) {
          localStorage.removeItem(key)
        }
      }
    },
    handleBeforeUnload(event) {
      if (this.debouncedPersistChanges) this.debouncedPersistChanges.flush()
      if (this.hasActiveChanges) {
        event.preventDefault()
        event.returnValue = ''
      }
    },
    refreshPage() {
      this.changes = []
      localStorage.removeItem(`changes_${this.selectedBatch.id}`)
      location.reload()
    },
    async noteUpdate(item) {
      const payload = [
        {
          id: item.id,
          promotion_price: item.promotion_price,
          multiplier: item.multiplier,
          note: item.note,
          net_gross_profit: item.net_gross_profit,
        }
      ]
      try {
        await Items.tprUpdate(payload)
        this.markBatchDataChanged()
        this.notify('Note updated successfully')
      } catch (err) {
        console.error(err)
        const message = err?.response?.data?.message || err.message
        this.alert(`Error updating note: ${message}`)
      } finally {
        this.loadItems()
        this.loading = false
      }
    },
    async srpUpdate(item, srp) {
      if (this.isEditingDisabled) return
      if (!this.validateInputs(item)) return
      // trunc SRP to 2 decimals without rounding
      if (srp !== '' && srp !== null && srp !== undefined) {
        srp = this.truncateToTwoDecimals(srp)
        item.promotion_price = srp
      }
      this.loading = true
      let payload = []
      if (this.selected.length) {
        this.selected.forEach(async item => {
            item.promotion_price = this.truncateToTwoDecimals(this.srp)
            item.net_gross_profit = await this.calculateGrossProfit(item)
        })
      } else {
        payload = [
          {
            id: item.id,
            promotion_price: srp,
            multiplier: item.multiplier,
            note: item.note,
            net_gross_profit: await this.calculateGrossProfit(item),
          }
        ]
      }
      // Logic in place to seperate between a Store User and an Ad group User -- specific flag: this.isTprProRetailUser
      if(!this.isTprProRetailUser){
        try {
          await Items.tprUpdate(payload)
          this.markBatchDataChanged()
          this.selected = []
          this.srp = ''
          this.srpMenu = false
          this.notify('SRP updated successfully')
          this.$gtag.event('srp_edit', { level: 'ad_group', batch_type: this.selectedBatch?.batch_type })
        } catch (err) {
          console.error(err)
          const message = err?.response?.data?.message || err.message
          this.alert(`Error updating SRP: ${message}`)
        } finally {
          this.loadItems()
          this.loading = false
        }
      } else {
        if (await this.storeSrpMultiplierUpdate(payload)) {
          this.$gtag.event('srp_edit', { level: 'store', batch_type: this.selectedBatch?.batch_type })
        }
      }
    },
    async storeSrpMultiplierUpdate(payloads) {
      try {
        let response
        if (!this.storeGroupIsSelected) {
          response = await Rules.storeSrpMultiplier(payloads)
        } else {
          let altItemPayloads = []
          const unresolvedCascade = []
          const explicitIds = new Set(payloads.map(p => p.id))
          payloads.forEach(payload => {
            // Resolve through the full set: `items` holds one page, so a row edited on another
            // page used to fall through here and silently skip its child stores.
            const originalItem = this.itemsById.get(payload.id)
            if (!originalItem) {
              unresolvedCascade.push(payload.id)
              return
            }
            if (this.propagateChangesToChildRows && originalItem.alternate_store_items?.length > 0) {
              originalItem.alternate_store_items.forEach(asi => {
                if (explicitIds.has(asi.id)) return
                const altItemPayload = {
                  ...payload,
                  id: asi.id
                }
                altItemPayloads.push(altItemPayload)
              })
            }
          })
          // Writing the rest would update the focal store and leave its child stores on the
          // old price -- the half-applied state this branch exists to remove. Refuse the
          // whole write instead, before it is sent.
          if (unresolvedCascade.length && this.propagateChangesToChildRows) {
            console.error('Cascade aborted, rows could not be resolved:', unresolvedCascade)
            this.alert(`${unresolvedCascade.length} item(s) could not be applied to the other stores in this group. Nothing was saved. Please reload the batch and retry.`)
            return
          }
          response = await Rules.storeSrpMultiplier([...payloads, ...altItemPayloads])
        }
        this.markBatchDataChanged()
        const ok = !(response.data && response.data.message) || response.data.message === 'TPR successfully updated.'
        if (response.data && response.data.message) {
          if (response.data.message === 'TPR successfully updated.') {
            this.notify(response.data.message)
          } else {
            this.alert(response.data.message)
          }
        } else {
          this.notify('Item updated successfully')
        }
        this.selected = []
        this.srp = ''
        this.srpMenu = false
        return ok
      } catch (err) {
        console.error('store side srp & multiplier error: ', err)
        const message = err?.response?.data?.message || err.message
        this.alert(`Error updating store items: ${message}`)
        return false
      } finally {
        await this.getItems()
      }
    },
    async multiplierUpdate(item, multiplier) {
      if (this.isEditingDisabled) return
      if (!this.validateInputs(item)) return
      const payload = [
        {
          id: item.id,
          promotion_price: item.promotion_price,
          multiplier: multiplier,
          note: item.note,
          net_gross_profit: item.net_gross_profit
        }
      ]
      try {
        await Items.tprUpdate(payload)
        this.markBatchDataChanged()
        this.notify('Multiplier updated successfully')
      } catch (err) {
        console.error(err)
        const message = err?.response?.data?.message || err.message
        this.alert(`Error updating multiplier: ${message}`)
      } finally {
        await this.loadItems()
        this.loading = false
      }
    },
    async handleInput(item, event, field) {
      if (this.isEditingDisabled) return
      if (!event || !event.target) {
        console.error('Invalid event object:', event)
        return
      }
      let value = event.target.value
      const existingChange = this.changes.find(change => change.id === item.id)
      if (existingChange) {
        existingChange[field] = value
      } else {
        const originalItem = this.originalItems.find(origItem => origItem.id === item.id)
        if (!originalItem) {
          this.originalItems.push(Object.freeze({ ...item }))
        }
        this.changes.push({ id: item.id, [field]: value, item_id: item.item_id })
      }
      item[field] = value
      // SRP edits set a single-unit price; drop a stale inherited multiplier unless set this edit
      if (field === 'promotion_price' && Number(item.multiplier) > 1) {
        const change = this.changes.find(c => c.id === item.id)
        if (change && change.multiplier === undefined) {
          item.multiplier = 1
          change.multiplier = 1
        }
      }
      this.debouncedUpdateGrossProfit(item)
    },
    async prepareToSave() {
      if (this.isEditingDisabled) return
      let priceOutsideOfCompliance = false
      // Warn NON-ARG Manager users if saving SRP values outside of compliance
      if (this.isTprProRetailUser) {
        for (const change of this.changes) {
          const item = this.effectiveRow(change)
          if (item && item.enforce_compliance && !this.srpIsCompliant(item)) {
            priceOutsideOfCompliance = true
            break
          }
        }
        if (priceOutsideOfCompliance) {
          this.showConfirmPopup = true
          return
        } 
      }
      await this.saveChanges()
    },
    cancelConfirm() {
      this.loading = false
      this.confirmChecked = false
      this.showConfirmPopup = false
    },
    async saveChanges() {
      if (this.isEditingDisabled) return
      this.showConfirmPopup = false
      this.confirmChecked = false
      this.loading = true
      const payloads = []
      const unresolvedChanges = []
      for (const change of this.changes) {
        const item = this.effectiveRow(change)
        if (!item) {
          unresolvedChanges.push(change.id)
          continue
        }
        payloads.push({
          id: item.id,
          promotion_price: Number(item.promotion_price),
          multiplier: Number(item.multiplier),
          note: item.note,
          net_gross_profit: item.net_gross_profit,
        })
      }
      // A change we cannot resolve is a dropped edit. Refuse the whole save: this method
      // clears `changes` when it finishes, so saving the rest would bin the unresolved edit
      // with nothing left to retry. Returning here keeps every pending change intact, and
      // getItems reapplies them on reload.
      if (unresolvedChanges.length) {
        console.error('Save aborted, rows could not be resolved for pending changes:', unresolvedChanges)
        this.alert(`${unresolvedChanges.length} changed item(s) could not be saved. Nothing was saved. Please reload the batch and retry.`)
        this.loading = false
        return
      }
      if (payloads.length > 0 && !this.isTprProRetailUser) {
        try {
          await Items.tprUpdate(payloads)
          this.markBatchDataChanged()
          this.notify('Changes saved successfully')
          this.$gtag.event('batch_save', { changed_count: payloads.length, batch_type: this.selectedBatch?.batch_type })
        } catch (err) {
          console.error(err)
          const message = err?.response?.data?.message || err.message
          this.alert(`Error updating items: ${message}`)
        } finally {
          this.loadItems()
        }
      } else if (this.isTprProRetailUser && payloads.length > 0) {
        if (await this.storeSrpMultiplierUpdate(payloads)) {
          this.$gtag.event('batch_save', { changed_count: payloads.length, batch_type: this.selectedBatch?.batch_type })
        }
      }
      this.changes = []
      this.originalItems = []
      this.selected = []
      this.srp = ''
      this.srpMenu = false
      this.loading = false
    },
    async shuttleChanges(wholeBatch = false) {
      this.loading = true
      if (wholeBatch) {
        let response
        if (this.isTprProRetailUser) {
          const storeIds = this.storeGroupIsSelected
            ? this.selectedStoreParty.children.flatMap(p => p.store_party_id)
            : [this.selectedStore.store_party_id]
          response = await Rules.getChangedItems(storeIds, this.selectedBatch.tpr_batch_id)
        } else {
          response = await Items.getChangedItems(this.selectedBatch.id)
        }
        const seenItemKeys = new Set()
        this.changesAvailableForShuttle = response.data
          .filter(item => {
            const key = `${item.item_id}|${item.promotion_price}|${item.multiplier}`
            if (seenItemKeys.has(key)) return false
            seenItemKeys.add(key)
            return true
          })
          .map(item => ({
            upc: item.upc,
            itemId: item.item_id,
            itemDescription: item.description,
            multiplier: item.multiplier,
            srp: item.promotion_price,
            net_gross_profit: item.net_gross_profit,
            excluded: item.excluded,
            changedColumns: [],
          }))
      }
      else {
        this.changesAvailableForShuttle = this.changes.map(change => {
          const item = this.effectiveRow(change)
          if (!item) {
            console.error(`Missing item data for change ID: ${change.id}`)
            return null
          }
          const multiplier = change.multiplier !== undefined ? change.multiplier : item.multiplier
          const srp = change.promotion_price !== undefined ? change.promotion_price : item.srp
          const net_gross_profit = change.net_gross_profit !== undefined ? change.net_gross_profit : item.net_gross_profit
          const excluded = change.excluded !== undefined ? change.excluded : item.excluded
          const changedColumns = []
          if (change.multiplier !== undefined) changedColumns.push('multiplier')
          if (change.promotion_price !== undefined) changedColumns.push('srp')
          if (change.net_gross_profit !== undefined) changedColumns.push('net_gross_profit')
          if (change.excluded !== undefined) changedColumns.push('excluded')
          return {
            upc: item.upc,
            itemId: item.item_id,
            itemDescription: item.description,
            multiplier,
            srp,
            net_gross_profit,
            excluded,
            changedColumns,
            ...change
          }
        }).filter(Boolean)
      }
      this.adShuttleDialog = true
      this.loading = false
    },
    confirmShuttleChanges() {
      this.$emit('confirmShuttleChanges')
      this.adShuttleDialog = true
    },
    cancelShuttleChanges() {
      this.$emit('cancelShuttleChanges')
    },
    openBulkMenu() {
      this.srpMenu = true
      this.multiplier = 1
    },
    async bulkUpdate() {
      if (this.isEditingDisabled) return
      // Truncate SRP to 2 decimal places (no rounding)
      if (this.srp !== '' && this.srp !== null && this.srp !== undefined) {
        this.srp = this.truncateToTwoDecimals(this.srp)
      }
      if (this.srp <= 0 || this.multiplier <= 0) {
        this.alert("SRP and Multiplier must be greater than zero.")
        return
      }
      if (this.tprProTenants) {
        console.log("Bulk applying")
        this.bulkApply()
      } else {
        this.bulkSave()
      }
    },
    async bulkSave() {
      this.loading = true
      let payload = await Promise.all(this.selected.map(async item => {
        const promo_price = this.srp ? this.truncateToTwoDecimals(this.srp) : this.truncateToTwoDecimals(item.promotion_price)
        const mult = this.multiplier ? this.multiplier : item.multiplier
        return {
          id: item.id,
          promotion_price: promo_price,
          multiplier: mult,
          note: item.note,
          net_gross_profit: await this.calculateGrossProfit({...item, promotion_price: promo_price, multiplier: mult})
        }
      }))
      if (!this.isTprProRetailUser) {
        try {
          await Items.tprUpdate(payload)
          this.markBatchDataChanged()
          this.selected = []
          this.srp = ''
          this.srpMenu = false
          this.notify('TPR updated successfully')
          this.$gtag.event('bulk_action', { action: 'save' })
        } catch (err) {
          console.error(err)
          const message = err?.response?.data?.message || err.message
          this.alert(`Error updating TPR: ${message}`)
        } finally {
          this.loadItems()
          this.loading = false
        }
      } else {
        this.storeSrpMultiplierUpdate(payload).then(ok => {
          if (ok) this.$gtag.event('bulk_action', { action: 'save' })
        })
      }
    },
    async bulkApply() {
      if (this.isEditingDisabled) return
      // Applies data to the SRP input boxes, but doesn't save it in the backend. Must use save button to save in DB
      for (const item of this.selected) {
        const newSRP = this.srp ? this.truncateToTwoDecimals(this.srp) : this.truncateToTwoDecimals(item.promotion_price)
        const newMult = this.multiplier ? this.multiplier : item.multiplier
        const existingChange = this.changes.find(change => change.id === item.id)
        const grossProfitItem = {...item, promotion_price: newSRP, multiplier: newMult}
        if (existingChange) {
          existingChange['promotion_price'] = newSRP
          existingChange['multiplier'] = newMult
          existingChange['net_gross_profit'] = await this.calculateGrossProfit(grossProfitItem)
        } else {
          const originalItem = this.originalItems.find(origItem => origItem.id === item.id)
          if (!originalItem) {
            this.originalItems.push(Object.freeze({ ...item }))
          }
          this.changes.push({ id: item.id, promotion_price: newSRP, multiplier: newMult, item_id: item.item_id, net_gross_profit: await this.calculateGrossProfit(grossProfitItem) })
        }
        item.promotion_price = newSRP
        item.multiplier = newMult
      }
      this.selected = []
      this.srp = ''
      this.multiplier = ''
      this.srpMenu = false
      this.notify('TPR Updated - Save changes to confirm')
      this.$gtag.event('bulk_action', { action: 'apply' })
      this.loadItems()
    },
    async bulkDelete() {
      if (this.isEditingDisabled) return
      this.loading = true
      try {
        await Promise.allSettled(
          this.selected.map(item => Items.tprDelete(item.id))
        )
        this.markBatchDataChanged()
        this.deleteMenu = false
        this.selected = []
        this.notify('TPRs deleted successfully')
        this.$gtag.event('bulk_action', { action: 'delete' })
      } catch (err) {
        console.error(err)
        const message = err?.response?.data?.message || err.message
        this.alert(`Error deleting TPRs: ${message}`)
      } finally {
        this.loadItems()
      }
    },
    async showLinkedGroupModal(item) {
      if (item.promotion_price <= 0 || item.multiplier <= 0) {
        this.alert("SRP and Multiplier must be greater than zero.")
        return false
      }
      this.linkedGroupItem = item
      this.linkedGroupStore = null
      if (this.storeGroupIsSelected && item.store_id) {
        const storeForItem = this.selectedStoreParty.children.find(
          store => store.store_party_id === item.store_id || store.party_id === item.store_id
        )
        if (storeForItem) {
          this.linkedGroupStore = storeForItem
        } else {
          this.linkedGroupStore = this.primaryStore
        }
      } else {
        this.linkedGroupStore = this.primaryStore
      }
      this.linkedGroupModal = true
    },
    // Both the focal row and the cascaded table apply a linked group through here. The
    // write has already happened; what is left is to drop the pending edits it satisfied so
    // the toolbar stops asking for a save, and to reload, because the grey "edited" cell is
    // driven by the persisted version_id and only a refetch can see it.
    linkedGroupSave (changedItems) {
      // Keyed as strings: this replaced a loose != comparison, and a pending edit that
      // failed to clear here would leave the toolbar demanding a save that already happened.
      const savedIds = new Set(changedItems.map(item => String(item.id)))
      this.changes = this.changes.filter(change => !savedIds.has(String(change.id)))
      // The snapshots go with them. handleSubItemChange only takes a baseline when the row
      // has none, so a leftover pre-apply snapshot would silently become the target of the
      // next per-row reset -- restoring the price this apply replaced.
      this.originalItems = this.originalItems.filter(orig => !savedIds.has(String(orig.id)))
      this.markBatchDataChanged()
      this.getItems()
    },
    markBatchDataChanged() {
      this.batchDataVersion += 1
    },
    openAiInsights() {
      if (!this.showAiInsightsButton || this.aiInsightsBlockedReason) return
      this.aiInsightsDialog = true
      this.$gtag.event('ai_insights_open', { level: this.aiInsightsLevel, batch_type: this.selectedBatch?.batch_type })
    },
    // Called by AIInsightsModal when it needs a fresh report. Always the whole batch: the
    // on-screen filters are ignored.
    async fetchAiInsights() {
      const body = { level: this.aiInsightsLevel }
      if (this.isTprProRetailUser) {
        body.store_ids = this.aiInsightsStoreIds
        body.primary_store_id = this.primaryStore?.store_party_id || null
      }
      const response = await Items.getAiInsights(this.aiInsightsBatchId, body)
      return response.data
    },
    onAiInsightsGenerated(result) {
      if (result?.no_data) return
      this.$gtag.event('ai_insights_generate', { level: this.aiInsightsLevel, batch_type: this.selectedBatch?.batch_type })
    },
    onAiInsightsExported() {
      this.$gtag.event('ai_insights_export', { level: this.aiInsightsLevel, batch_type: this.selectedBatch?.batch_type })
    },
    beforeDestroy() {
      this.$root.$off("shuttleWholeBatch", this.handleShuttleWholeBatch)
      window.removeEventListener('beforeunload', this.handleBeforeUnload)
    },
    calculateDifferenceToRegularPrice(item) {
      if (!item.multiplier || item.multiplier <= 0) return 0
      const promotionPricePerUnit = (item.promotion_price || 0) / item.multiplier
      return promotionPricePerUnit - (item.retail_price || 0)
    },
    formatName(value) {
      if (value.length <= this.truncateAt) return value
      return value.substring(0, this.truncateAt) + "..."
    },
    srpIsDisabled(srpInputField) {
      return srpInputField?.hasAttribute('disabled')
    },
    srpIsCompliant(item) {
      if (item.enforce_compliance == false) return true
      const sig = `${item.promotion_price}|${item.multiplier}|${item.original_promotion_price}|${item.variance}|${item.rules_promotion_price}|${this.isTprProRetailUser}`
      return this.cachedByItem('srpCompliance', item, sig, () => {
        return !(parseFloat(item.promotion_price) > this.srpComplianceLimit(item))
      })
    },
    onMultiplierChange({ item, value }) {
      this.multiplierUpdate(item, value)
      this.blurActiveElement()
    },
    onHandleInput({ item, value, field }) {
      this.handleInput(item, { target: { value } }, field)
    },
    onSrpBlur({ item, event }) {
      this.handleSRPBlur(item, event)
    },
    onSrpChange({ item, value }) {
      this.srpUpdate(item, value)
      this.blurActiveElement()
    },
    onEnter({ index, field, event }) {
      this.handleEnterKey(index, field, event)
    }
  }
}
</script>
<style>
.table >>> tbody tr.v-data-table__expanded__content {
  background: #eeeeee;
  box-shadow: none;
}
.disabled-row {
  opacity: 0.5;
}
.yellow-row {
  background-color: yellow;
}
.v-data-table tbody tr.yellow-row:hover {
  background-color: yellow !important;
}
.headerModal {
  padding: 10px;
  margin: 0%
}
.flex-right {
  /* Block flex (not inline-flex) so the toolbar respects the grid's width and
     never spills past its right edge onto the filter drawer when both the left
     nav and the filter drawer are open. */
  display: flex;
  width: 100%;
  justify-content: flex-end !important;
  gap: 10px;
  align-items: center;
  flex-wrap: nowrap;
}
.flex-right>* {
  /* Buttons keep their natural size (no text clipping); the keyword search
     below is the single elastic element that absorbs any squeeze. */
  flex-grow: 0;
  flex-shrink: 0;
  white-space: nowrap;
}
.flex-right>.pp-keyword-search {
  flex-shrink: 1;
  min-width: 90px;
}
.fixed-search-filters {
  width: 550px;
  flex-grow: 0;
  flex-shrink: 1;
  min-width: 100px;
  overflow: visible;
}
/* Filter drawer host: grid shrinks (scrolls) as the drawer pushes in, no overlap. */
.batch-flex {
  width: 100%;
}
.batch-grid {
  min-width: 0;
}
.pp-filter-drawer {
  align-self: stretch;
}
.header-column-name {
  text-align: center; 
  margin-bottom: 10px; 
  font-weight: bold; 
  max-width: 80%;
}
.list-group {
  margin-top: 10px;
  min-height: 100%
}
.list-group-item {
  padding: 8px; 
  border: 1px solid #e0e0e0;
  margin-bottom: 2px; 
  border-radius: 4px;
  max-width: 80%;
}
.centered-title {
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
}
.cursor-pointer {
  cursor: pointer;
}
.text-decoration-underline {
  text-decoration: underline;
}
.cursor-pointer:hover {
  opacity: 0.8;
}
/* Default value is 0.12 for the opacity */
.dark-dividers tbody tr td {
  border-bottom: thin solid rgba(0, 0, 0, 0.35) !important;
}
.dark-dividers tbody tr.v-data-table__expanded__content > td {
  border-bottom: 4px solid #64b5f6 !important;
}
</style>